import { Fragment, useMemo, useState } from 'react';
import { Button, Image, Input, Select, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, GiftOutlined, SplitCellsOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { StaffAsyncSelect } from './StaffAsyncSelect';
import { CopyLineValuesMenu } from './CopyLineValuesMenu';
import { SerialSwapDialog } from './SerialSwapDialog';
import { WarehousePickerDialog } from './WarehousePickerDialog';
import { ComboWarehousePickerDialog } from './ComboWarehousePickerDialog';
import { canPickWarehouse } from '../utils/warehousePick';
import { canSplitLine, siblingQuantity } from '../utils/splitLine';
import { posService } from '@/services/pos.service';
import { formatVND } from '@/utils/format';
import { usePosShiftStoreId } from '@/utils/posShift';
import { formatVatRate, normalizeVatRate, parseVatInput } from '../utils/vatInput';
import { itemGrossAmount, itemVatAmount, priceIncludingVat, recalcItem } from '../utils/orderItemCalc';
import { formatUnitPrice, formatUnitPriceInput, parseUnitPrice } from '../utils/unitPriceInput';
import {
  applyLineDiscountAmountPerUnit,
  applyLineDiscountPercent,
  applyLinePrice,
  applyLineQuantity,
} from '../utils/lineValueCalc';
import { POS_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { formatPolicyLabel, formatPolicyValue, getDeviationAmount, isDiscountDeviating } from '../utils/discountPolicy';
import {
  ComboComponentCell,
  comboComponentQty,
  type ComboComponent,
} from '@/components/Orders/ComboComponentsList';
import type { OrderItem, FulfillmentMode, PosVariantWarehouseStocksResponse } from '../types';
import type { PosItemError } from '../utils/validatePosItems';
import './OrderItemsTable.css';

const { Text } = Typography;

function formatPrice(n: number) {
  return formatUnitPrice(n);
}

/** TCR-2224 — tiền dòng đã làm tròn về đồng: không bao giờ có phần lẻ. */
function formatVND0(n: number): string {
  return Math.round(n).toLocaleString('vi-VN');
}

/** Strip non-digit + format thousand separators trong lúc gõ. */
function formatPriceInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return parseInt(digits, 10).toLocaleString('vi-VN');
}

/** Parse "1.000.000" → 1000000. Bỏ mọi ký tự không phải digit. */
function parsePrice(s: string): number {
  const digits = s.replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : 0;
}

/** Parse decimal cho CK% — giữ dấu chấm. */
function parseDecimal(s: string): number {
  return parseFloat(s.replace(/[^\d.]/g, '')) || 0;
}

/** Thành phần combo có thể chọn kho (loại hàng serial + chế độ Đặt trước — xem `canPickWarehouse`). */
function pickableComboMembers(members: OrderItem[], fulfillmentMode: FulfillmentMode): OrderItem[] {
  return members.filter((m) => canPickWarehouse(m, fulfillmentMode));
}

/** Nhãn nút "Chọn kho" ở dòng đầu cụm: tên kho nếu MỌI thành phần chọn được
 *  kho đều đã cùng một kho, ngược lại "Chọn kho" (chưa đồng nhất/chưa chọn). */
function comboWarehouseButtonLabel(pickable: OrderItem[]): string {
  if (pickable.length === 0) return 'Chọn kho';
  const first = pickable[0].warehouseName;
  const allSame = !!first && pickable.every((m) => m.warehouseName === first);
  return allSame ? first : 'Chọn kho';
}

/**
 * Cột của dòng đầu cụm — MỘT nguồn định nghĩa duy nhất, dùng CHUNG cho cả hàng
 * nội dung (Input/Button thật) lẫn hàng phụ (tiết kiệm: giá lẻ gạch ngang + tag
 * CK) trong `ComboGroupRow`.
 *
 * Trước đây hàng phụ chép tay MỘT DANH SÁCH DIV THỨ HAI, dừng lại sau cột CK —
 * thiếu hẳn các cột đuôi (VAT 72 + SL 72 + TL 52 + T.Tiền 96 + TT 36 ≈ 328px).
 * `style.width` của từng ô hàng phụ vẫn "đúng số" nên test kiểm bằng
 * `style.width` (đo RIÊNG từng ô, không so với header) xanh — nhưng vì hàng
 * phụ THIẾU hẳn các cột đuôi, tổng bề rộng phần cố định của nó nhỏ hơn hàng
 * chính/header đúng ~328px ⇒ cột "Tên Hàng" (`flex: 1`) của hàng phụ NUỐT hết
 * phần dư đó ⇒ mọi ô sau nó (Giá Tiền, CK) bị đẩy lệch PHẢI đúng ~328px trên
 * màn hình thật — chỉ thấy được khi đo vị trí thật (`getBoundingClientRect`)
 * trên trình duyệt, không thấy qua so sánh `style.width` của từng ô cắt rời.
 *
 * Khai TOÀN BỘ cột (kể cả cột không có nội dung ở hàng phụ) ở MỘT hàm duy
 * nhất rồi để cả 2 hàng `.map()` qua cùng mảng này — không còn "lần chép tay
 * thứ hai" để lệch. `className` đi kèm mỗi cột (vd. `pos-col-weight`,
 * `pos-col-discount-percent`) đảm bảo hành vi ẩn cột theo responsive
 * (`POSPage.css`, ẩn TL+CK% dưới 1280px) cũng đồng bộ giữa 2 hàng — thiếu
 * class ở MỘT hàng thì hàng đó không ẩn cùng lúc với hàng kia, lại lệch tiếp
 * ở màn hẹp dù đã sửa xong bug ở màn rộng.
 */
/**
 * TCR-1154 — bề rộng cột "Loại CK" + "Chiết khấu" (thay cột CK% + CK tách rời).
 * Dùng CHUNG cho header, dòng thường và cụm combo — lệch một nơi là cả bảng lệch cột.
 */
const DISCOUNT_TYPE_COL_WIDTH = 58;
const DISCOUNT_COL_WIDTH = 98;

/**
 * Bề rộng cột Giá Tiền/SL/VAT/T.Tiền — cùng lý do dùng hằng số như CK ở trên:
 * dùng CHUNG cho header, dòng thường và cụm combo, lệch một nơi là cả bảng
 * lệch cột. Đã thu hẹp so với bản gốc (130/72/88/96) để đóng khoảng thiếu hụt
 * gây thanh cuộn ngang. Đo THẬT qua Playwright ở chế độ Giao cho ĐVVC (chế độ
 * mặc định @1440, khít nhất — panel còn 660px) chứ không suy từ code: panel
 * 660px, tổng bề rộng dòng phải ≤660px kèm biên an toàn. Chừa dư ~20px.
 */
const PRICE_COL_WIDTH = 118;
/** TCR-2125: cột Giá gợi ý (B2B) đứng CẠNH Loại + Chiết khấu nên thu hẹp từ 184 → vừa giá + tag cấp. */
const SUGGEST_COL_WIDTH = 112;
/**
 * TCR-2475 — bề rộng cột "Tồn" (chỉ B2B). Dùng CHUNG cho header, dòng
 * thường, dòng cụm combo và dòng thành phần — lệch một nơi là cả bảng lệch
 * cột (cùng lý do dùng hằng số chung như `SUGGEST_COL_WIDTH` ở trên).
 *
 * 72px là đủ cho "999" + đệm; số tồn lớn hơn thì ô tự co (giống hành vi ô
 * SL/T.Tiền đã dùng `max()` động). Cột này CHỈ có ở B2B ⇒ bảng bán lẻ giữ
 * nguyên bề rộng cũ, không phát sinh thanh cuộn ngang mới.
 */
const STOCK_COL_WIDTH = 72;
const QTY_COL_WIDTH = 38;
const VAT_COL_WIDTH = 66;
const TOTAL_COL_WIDTH = 62;
const INDEX_COL_WIDTH = 26;
const DELETE_COL_WIDTH = 26;

/**
 * TCR-1209 (đợt 2) — SL/T.Tiền vẫn tràn khi dữ liệu dài hơn mức đã đo ở
 * `QTY_COL_WIDTH`/`TOTAL_COL_WIDTH` (vd SL 4 chữ số, T.Tiền ≥ 9 chữ số).
 * Không thể cứ tăng 2 hằng số trên vì bề rộng dòng đã bị khoá sát ngân sách
 * 660px (xem comment phía trên) — thay vào đó GIÃN cột theo ĐÚNG độ dài dữ
 * liệu đang có trong giỏ, dùng CSS `max(<sàn>px, <N>ch + đệm)`: đơn vị `ch`
 * để trình duyệt tự đo theo font thật đang dùng (không đoán px/ký tự thủ
 * công), `max()` giữ nguyên bề rộng cũ khi dữ liệu ngắn (không đổi hành vi
 * đã verify trước đó). Tính MỘT LẦN cho CẢ bảng (không phải riêng từng
 * dòng) rồi dùng chung cho header + mọi dòng — nếu để mỗi dòng tự giãn
 * riêng, các cột sẽ lệch nhau giữa các dòng (cùng lý do buộc dùng hằng số
 * chung ở trên).
 */
const QTY_COL_WIDTH_BUFFER_PX = 16;
const TOTAL_COL_WIDTH_BUFFER_PX = 8;

/** TCR-2224 — chú thích dưới T.Tiền: cùng độ rộng với số tiền phía trên để vừa cột; nghĩa "đã gồm VAT" nằm ở tooltip. */
const LINE_TOTAL_CAPTION_TITLE = 'Thành tiền gồm VAT (làm tròn)';
const lineTotalCaption = (lineTotal: number): string => `= ${formatVND0(lineTotal)}`;

/** Export riêng cho test (jsdom không parse được `max()`/`calc()` lồng nhau
 *  trong `style.width`, nên test unit gọi thẳng hàm thuần này thay vì đọc
 *  style đã render — xem `OrderItemsTable.dynamicColWidth.test.tsx`). */
export function computeDynamicColWidths(
  items: OrderItem[],
  /** TCR-2224 — tiền dòng gồm VAT: caption `= N` dưới T.Tiền cũng phải vừa cột. */
  lineTotalById?: ReadonlyMap<string, number>,
): {
  qtyColWidth: string;
  totalColWidth: string;
} {
  let maxQtyChars = 0;
  let maxTotalChars = 0;
  const track = (qty: number, total: number, id?: string) => {
    maxQtyChars = Math.max(maxQtyChars, String(qty).length);
    maxTotalChars = Math.max(maxTotalChars, formatPrice(total).length);
    const lineTotal = id === undefined ? undefined : lineTotalById?.get(id);
    if (lineTotal !== undefined) {
      maxTotalChars = Math.max(maxTotalChars, lineTotalCaption(lineTotal).length);
    }
  };
  for (const block of groupItemsForRender(items)) {
    if (block.kind === 'loose') {
      track(block.item.quantity, block.item.totalAmount, block.item.id);
      continue;
    }
    // Cùng công thức unitCount/comboTotal dùng để HIỂN THỊ ở `ComboGroupRow`
    // (xem component đó) — chỉ dùng lại ở đây để ƯỚC LƯỢNG độ dài chữ số cho
    // mục đích đo cột, không phải nguồn tính tiền thật.
    const head = block.members[0];
    const unitCount = head.comboUnitCount && head.comboUnitCount > 0 ? head.comboUnitCount : 1;
    const comboAppliedUnitPrice = head.comboAppliedUnitPrice ?? head.comboUnitPrice ?? 0;
    const comboDiscountAmount = head.comboDiscountAmount ?? 0;
    const comboTotal = comboAppliedUnitPrice * unitCount - comboDiscountAmount;
    track(unitCount, comboTotal);
    for (const member of block.members) {
      track(member.quantity, member.totalAmount, member.id);
    }
  }
  return {
    qtyColWidth: `max(${QTY_COL_WIDTH}px, calc(${maxQtyChars}ch + ${QTY_COL_WIDTH_BUFFER_PX}px))`,
    totalColWidth: `max(${TOTAL_COL_WIDTH}px, calc(${maxTotalChars}ch + ${TOTAL_COL_WIDTH_BUFFER_PX}px))`,
  };
}

/**
 * Giá gốc + phần chiết khấu combo rơi vào MỘT dòng thành phần.
 *
 * Cơ số giá gốc PHẢI là snapshot cấu hình combo (`comboComponentRetailPrice`),
 * KHÔNG phải `retailPrice` (giá lẻ tươi của biến thể). Hai số đó lệch nhau bất
 * kỳ lúc nào bảng giá đổi sau khi combo được cấu hình, mà cụm phía trên lại
 * tính giá gốc/chiết khấu từ `comboRetailTotal` — cũng là snapshot. Lấy giá
 * tươi cho dòng thành phần thì chiết khấu các dòng cộng lại KHÔNG ra chiết
 * khấu của cụm (đã thấy trên dữ liệu thật: giá tươi 15.000.001 so với snapshot
 * 150.000 ⇒ dòng hiện CK 99%, cụm hiện 27%).
 *
 * Dòng dựng lại từ đơn ĐÃ LƯU không có snapshot per-line, nhưng luôn có
 * `comboRetailTotal` + `comboUnitPrice`: ở NHÁNH FALLBACK của
 * `resolveComboLinePrices` (record chưa backfill `price_in_combo`, rơi về
 * `priceComboComponents`) `price = round(giá combo × giá lẻ / tổng giá lẻ)`, suy ngược
 * `giá lẻ ≈ price × comboRetailTotal / comboUnitPrice` (sai số làm tròn vài
 * đồng, chỉ dùng để HIỂN THỊ). Không đủ dữ liệu thì coi như không giảm giá và
 * để trống hai ô CK — thà trống còn hơn bịa một mức chiết khấu.
 *
 * SNAPSHOT (`comboComponentRetailPrice`) LUÔN được TIN THẲNG khi tồn tại — kể
 * cả khi = 0đ. Trước đây điều kiện `snapshot > 0` coi 0đ giống hệt "không có
 * snapshot" rồi rơi xuống nhánh suy ngược, và cả hai nhánh còn kẹp
 * `candidate > member.price` mới nhận — combo TOÀN thành phần 0đ (vd
 * "ZEROTEST-ALL") được nhánh fallback của `resolveComboLinePrices`
 * (`priceComboComponents`) chia ĐỀU giá combo xuống từng
 * dòng (vd 150.000 combo, 2 thành phần → mỗi dòng bán 75.000 dù giá lẻ cấu
 * hình là 0đ) — dòng thành phần vì vậy hiện nhầm "Giá Tiền: 75.000" (= giá
 * đang bán) thay vì đúng giá lẻ đã cấu hình (0đ). Chỉ nhánh SUY NGƯỢC
 * (`derived`, dữ liệu ước tính chứ không phải cấu hình thật) mới còn cần kẹp
 * để tránh bịa một mức chiết khấu từ sai số làm tròn.
 */
function comboMemberPricing(
  member: OrderItem,
  combo: { retailUnitTotal: number; comboUnitPrice: number },
): { unitRetail: number; discountAmount: number; discountPercent: number } {
  const snapshot = member.comboComponentRetailPrice;
  let unitRetail: number;
  if (snapshot != null) {
    unitRetail = snapshot;
  } else {
    const derived =
      combo.retailUnitTotal > 0 && combo.comboUnitPrice > 0
        ? Math.round((member.price * combo.retailUnitTotal) / combo.comboUnitPrice)
        : 0;
    unitRetail = derived > member.price ? derived : member.price;
  }
  const unitDiscount = unitRetail - member.price;
  return {
    unitRetail,
    discountAmount: unitDiscount * member.quantity,
    discountPercent:
      unitRetail > 0 ? Math.round((unitDiscount / unitRetail) * 100 * 100) / 100 : 0,
  };
}

interface ComboGroupColumn {
  key: string;
  /** Cột co giãn DUY NHẤT ("Tên Hàng") — mọi cột khác dùng `width` cố định. */
  flex?: number;
  minWidth?: number;
  /** SL/T.Tiền truyền chuỗi CSS `max(...)` động (xem `computeDynamicColWidths`) — các cột còn lại vẫn số px cố định. */
  width?: number | string;
  className?: string;
}

function comboGroupColumns(
  showSerialCol: boolean,
  showWeightCol: boolean,
  isB2B: boolean,
  qtyColWidth: string,
  totalColWidth: string,
): ComboGroupColumn[] {
  return [
    { key: 'index', width: INDEX_COL_WIDTH },
    // TCR-2470: nút xoá ngay sau STT — để ở cuối thì phải cuộn ngang mới thấy.
    { key: 'delete', width: DELETE_COL_WIDTH },
    { key: 'name', flex: 1, minWidth: 160 },
    ...(showSerialCol ? [{ key: 'serial', width: 88 }] : []),
    { key: 'price', width: PRICE_COL_WIDTH },
    { key: 'qty', width: qtyColWidth },
    // TCR-2475: B2B thêm cột "Tồn" ngay SAU cột SL (thu ngân thấy tồn kho
    // đã chọn ngay cạnh số lượng, không phải mở modal mới biết).
    ...(isB2B ? [{ key: 'stock', width: STOCK_COL_WIDTH }] : []),
    // TCR-2125: B2B có thêm Giá gợi ý nhưng VẪN giữ Loại + Chiết khấu.
    ...(isB2B ? [{ key: 'suggest', width: SUGGEST_COL_WIDTH }] : []),
    { key: 'discountType', width: DISCOUNT_TYPE_COL_WIDTH },
    { key: 'discount', width: DISCOUNT_COL_WIDTH, className: 'pos-col-discount-percent' },
    { key: 'total', width: totalColWidth },
    ...(showWeightCol ? [{ key: 'weight', width: 52, className: 'pos-col-weight' }] : []),
    { key: 'vat', width: VAT_COL_WIDTH },
  ];
}

/**
 * Lưới cột ĐẦY ĐỦ của một dòng bảng POS — khớp từng cột với `TableHeader`,
 * kể cả N.Bán / C.Kho mà `comboGroupColumns()` không có.
 *
 * Dùng cho HÀNG THÀNH PHẦN của combo thật. Vì sao phải đủ cột dù hàng đó chỉ
 * điền hai ô (Tên Hàng, SL): cột "Tên Hàng" là `flex: 1`, nó nuốt mọi khoảng
 * trống còn lại của DÒNG. Bỏ bớt cột đuôi ⇒ tên giãn rộng hơn dòng cha ⇒ ô SL
 * bị đẩy sang phải và không còn thẳng cột SL nữa — đúng thứ cần sửa ở đây.
 */
function comboComponentColumns(opts: {
  showSerialCol: boolean;
  showWeightCol: boolean;
  showSellerCol: boolean;
  showWarehouseCol: boolean;
  isB2B: boolean;
  qtyColWidth: string;
  totalColWidth: string;
}): ComboGroupColumn[] {
  return [
    { key: 'index', width: INDEX_COL_WIDTH },
    // TCR-2470: nút xoá ngay sau STT — để ở cuối thì phải cuộn ngang mới thấy.
    { key: 'delete', width: DELETE_COL_WIDTH },
    { key: 'name', flex: 1, minWidth: 160 },
    ...(opts.showSerialCol ? [{ key: 'serial', width: 88 }] : []),
    { key: 'price', width: PRICE_COL_WIDTH },
    { key: 'qty', width: opts.qtyColWidth },
    // TCR-2475: B2B có cột "Tồn" ngay sau SL — phải khai đủ ở MỌI lưới cột
    // (dòng thường, cụm combo, thành phần combo) thì header/dòng mới thẳng
    // cột, không lệch nhau.
    ...(opts.isB2B ? [{ key: 'stock', width: STOCK_COL_WIDTH }] : []),
    ...(opts.isB2B ? [{ key: 'suggest', width: SUGGEST_COL_WIDTH }] : []),
    { key: 'discountType', width: DISCOUNT_TYPE_COL_WIDTH },
    { key: 'discount', width: DISCOUNT_COL_WIDTH, className: 'pos-col-discount-percent' },
    { key: 'total', width: opts.totalColWidth },
    ...(opts.showWeightCol ? [{ key: 'weight', width: 52, className: 'pos-col-weight' }] : []),
    { key: 'vat', width: VAT_COL_WIDTH },
    ...(opts.showSellerCol ? [{ key: 'seller', width: 128 }] : []),
    ...(opts.showWarehouseCol ? [{ key: 'warehouse', width: 128 }] : []),
  ];
}

/**
 * Một thành phần combo = một DÒNG của bảng giỏ hàng (không phải bảng lồng có
 * lưới cột riêng): ô Tên Hàng mang ảnh·tên·SKU, ô SL mang số lượng đã nhân theo
 * số combo trong giỏ. Các ô tiền để TRỐNG — thành phần không có giá riêng, mọi
 * con số tiền nằm ở dòng combo cha. CHỈ XEM: không input, không nút.
 */
function ComboComponentRow({ component, multiplier, columns, indent, testId }: {
  component: ComboComponent;
  multiplier: number;
  columns: ComboGroupColumn[];
  indent: number;
  testId: string;
}) {
  const cell: React.CSSProperties = {
    padding: '6px 4px',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  return (
    <div
      data-testid={testId}
      style={{ display: 'flex', background: '#fafafa', borderBottom: '1px solid #f5f5f5', color: '#666' }}
    >
      {columns.map((col) => {
        const style: React.CSSProperties = col.flex
          ? { ...cell, flex: col.flex, minWidth: col.minWidth }
          : { ...cell, width: col.width };
        if (col.key === 'name') {
          return (
            <div key={col.key} style={{ ...style, paddingLeft: indent }}>
              <ComboComponentCell component={component} imageSize={32} />
            </div>
          );
        }
        if (col.key === 'qty') {
          return (
            <div key={col.key} style={{ ...style, justifyContent: 'center', fontWeight: 600, color: '#595959' }}>
              {comboComponentQty(component, multiplier)}
            </div>
          );
        }
        return <div key={col.key} className={col.className} style={style} />;
      })}
    </div>
  );
}

/**
 * Nút mũi tên bung/thu danh sách thành phần của một dòng COMBO THẬT — đặt
 * TRƯỚC ảnh sản phẩm, đúng chỗ mũi tên của cụm combo ẢO, để hai loại combo mở
 * ra bằng cùng một thao tác ở cùng một vị trí.
 */
function ComboExpandToggle({ expanded, onToggle, testId }: {
  expanded: boolean;
  onToggle: () => void;
  testId: string;
}) {
  return (
    <Button
      type="text"
      size="small"
      data-testid={testId}
      aria-label={expanded ? 'Đóng danh sách thành phần combo' : 'Mở danh sách thành phần combo'}
      aria-expanded={expanded}
      onClick={onToggle}
      style={{ flex: 'none', padding: 0, width: 18, height: 18, minWidth: 18, color: '#a4193d' }}
    >
      {expanded ? '▾' : '▸'}
    </Button>
  );
}

interface Props {
  items: OrderItem[];
  /**
   * TCR-2058 — VAT từng dòng SAU khi phân bổ voucher/KM/điểm (cùng số BE lưu và
   * lên HĐĐT), khoá theo item id. `itemVatAmount` chỉ là VAT TRƯỚC phân bổ nên
   * chỉ làm fallback khi không truyền / thiếu id.
   */
  lineVatById?: ReadonlyMap<string, number>;
  /**
   * TCR-2224 — tiền cả dòng đã gồm VAT, làm tròn về đồng (`line_total`): tổng khách trả
   * = Σ số này. Khoá theo item id. Không truyền / thiếu id ⇒ không hiện (đơn cũ).
   */
  lineTotalById?: ReadonlyMap<string, number>;
  fulfillmentMode: FulfillmentMode;
  storeId?: string;
  onUpdateItem: (id: string, patch: Partial<OrderItem>) => void;
  onRemoveItem: (id: string) => void;
  /**
   * TCR-2614 — "Tách dòng": chèn ngay dưới dòng `id` một dòng cùng SKU/kho, SL 1, giá 0 (hàng
   * tặng). Không truyền ⇒ không hiện nút (màn chỉ xem).
   */
  onSplitItem?: (id: string) => void;
  /**
   * Thu ngân bỏ giá flash của một dòng, trả về giá niêm yết. Không truyền ⇒
   * không hiện nút bỏ (màn chỉ xem / màn không wiring flash sale).
   */
  onDropFlash?: (id: string) => void;
  /**
   * Trần số lượng cho một dòng, `undefined` = không khoá. Dùng ở màn sửa đơn
   * cho dòng flash sale mà chương trình ĐÃ kết thúc: giữ nguyên giá đã bán
   * (đơn chốt giá đó với khách rồi) nhưng không cho bán thêm hàng giá flash
   * ngoài khung giờ.
   */
  maxQuantityFor?: (item: OrderItem) => number | undefined;
  /**
   * TCR-1423: mỗi lần đổi SL ở dòng thuộc nhóm flash sale (dòng flash gốc
   * HOẶC dòng dư sinh từ lần tách trước), phân bổ lại suất KM cho CẢ NHÓM
   * thay vì cấp giá flash cho toàn bộ SL. Không truyền ⇒ giữ hành vi cũ
   * (`onUpdateItem` nhân thẳng giá flash cho cả SL, kể cả phần vượt).
   */
  onRebalanceFlashGroup?: (id: string, requestedQuantity: number) => void;
  onSwapSerial: (itemId: string, serial: string) => Promise<{ ok: boolean; error?: string }>;
  swapping?: boolean;
  staleItemIds?: Set<string>;
  /** Preview prices for stale items (item.id → new price computed by BE).
   *  Used to render "old → new" inline so cashier sees the impending change
   *  before clicking "Cập nhật giá" in the banner. */
  stalePreviewByItemId?: Map<string, { price: number }>;
  /** Per-item required-field errors keyed by item id (seller / warehouse). */
  itemErrors?: Record<string, PosItemError>;
  /** Tắt khi tenant không bật quản lý theo serial — bỏ hẳn cột Mã QC. */
  showSerialCol: boolean;
  /** Tắt khi tenant không bật hiển thị cột TL — bỏ hẳn cột trọng lượng. */
  showWeightCol: boolean;
  /** TCR-629: scope modal chọn kho theo store này (màn sửa đơn — store của đơn).
   *  Bỏ trống → store của ca hiện tại (interceptor inject). */
  warehouseStoreId?: string;
  /** KH tổ chức (B2B): cột CK%/CK → Giá gợi ý; Giá Tiền = Giá áp dụng. */
  isB2B?: boolean;
  /** TCR-2129 — bật hiển thị CS/cảnh báo CK khác chính sách (khách kênh Sỉ/GT/MT). */
  discountPolicyEnabled?: boolean;
  /** TCR-2129 — tên người đang thao tác, hiện ở tooltip khi dòng chưa có discountDeviationBy. */
  currentUserName?: string;
  /** Quyền pos.discount — false thì input Price/CK%/CK-amount disabled cho cả hàng thường lẫn combo. */
  hasDiscountPermission: boolean;
  /** Tách cụm combo về dòng lẻ giá gốc. */
  onSplitCombo?: (comboGroupId: string) => void;
  /** Xoá cả cụm khỏi giỏ. */
  onRemoveCombo?: (comboGroupId: string) => void;
  /** Đổi số combo của cụm. */
  onChangeComboCount?: (comboGroupId: string, nextCount: number) => void;
  /**
   * Số combo tối đa gộp được từ hàng ĐANG CÓ trong giỏ. Chỉ dùng để chọn
   * tooltip — TCR-982 bỏ kẹp trần theo số này (xem `handleComboCountBlur`).
   */
  comboMaxCount?: (comboGroupId: string) => number;
  /**
   * TCR-982: cụm đang chờ tra tồn để tăng số combo — khoá ô SL của riêng cụm đó
   * trong lúc chờ, tránh gõ số thứ hai chồng lên request đang bay.
   */
  comboCountPendingGroupId?: string | null;
  /**
   * Vô hiệu hoá cả 2 nút +/- của stepper số combo (không phải riêng nút nào).
   * BẬT trên màn sửa đơn: đổi số combo của một cụm ĐÃ LƯU đòi hỏi tách một
   * dòng đã lưu thành "phần ở lại combo" + "phần rời/gia nhập combo" — hợp
   * đồng update-order hiện tại không biểu diễn được việc này (mỗi
   * (sku, combo_group_id) chỉ khớp ĐÚNG MỘT dòng đã lưu), nên MỌI đổi số
   * (tăng lẫn giảm) trên một cụm đã tồn tại đều lưu thất bại — xem
   * `edit.tsx` (Task 13 fix round 2, Finding 2; giảm 400 do trùng
   * (sku, combo_group_id) giữa dòng rời/dòng ở lại; tăng 400 do
   * `resolveUnitCount` thấy 2 dòng cùng biến thể cho một cụm — đã kiểm
   * chứng thực nghiệm, không phải giả định). TẮT ở POS: tạo đơn mới không
   * có dòng đã lưu để đối chiếu, stepper hoạt động bình thường.
   */
  comboStepperDisabled?: boolean;
  /**
   * ẨN nút "Xoá cụm" (không phải disable). BẬT trên màn sửa đơn: xoá dòng
   * khỏi giỏ không đi kèm xoá dòng đã lưu — `updateOrder` không có ngữ nghĩa
   * xoá dòng, nên thao tác trông như thành công (giỏ cập nhật, lưu không lỗi)
   * nhưng KHÔNG hề persist; đơn tải lại vẫn còn nguyên cụm. Sau khi xoá
   * (Task 13 fix round 1) và stepper (fix round 2, Finding 2), con người đã
   * quyết định KHÔNG mở rộng hợp đồng update-order để hỗ trợ xoá dòng — gap
   * này là VĨNH VIỄN cho màn sửa đơn, không phải tạm thời. HIỆN (không phải
   * disable) vì không có hành động thay thế nào để trỏ tới qua tooltip —
   * "Tách" tách cụm về dòng lẻ chứ không xoá gì khỏi đơn — một nút disable
   * kèm tooltip sẽ chỉ là ngõ cụt. TẮT ở POS: xoá khỏi giỏ hàng CHƯA ghi hoá
   * đơn là thao tác client-side thuần tuý, hoạt động đúng.
   */
  hideComboRemove?: boolean;
  /**
   * Cụm combo mặc định MỞ (hiện sẵn các dòng thành phần) — trước đây hard-code
   * `useState(false)` trong `ComboGroupRow` khiến thu ngân phải bấm ▸ mới thấy
   * thành phần, và trên POS đó còn là nơi duy nhất chọn được kho cho từng
   * thành phần (xem nút "Chọn kho" ở dòng thành phần) nên đóng mặc định = giấu
   * luôn lối thoát khỏi blocker "chưa chọn kho xuất hàng". Expose qua prop
   * (mặc định `true`) thay vì hard-code lần nữa để nếu sau này màn khác dùng
   * chung `OrderItemsTable` cố ý muốn đóng mặc định thì chỉ cần truyền
   * `false`, không phải sửa lại component dùng chung.
   */
  comboDefaultExpanded?: boolean;
  /**
   * TCR-1143 (final review, finding #1): khoá sửa số lượng/giá/CK cho dòng
   * flash sale (`item.flash_sale_id` truthy) — CÙNG cơ chế khoá đã có sẵn
   * cho `item.is_gift`, nhưng KHÔNG bake thẳng vào component dùng chung vì lý
   * do khoá là đặc thù của MỘT trang, không phải bản chất của dòng flash sale
   * nói chung. `Orders/edit.tsx` lưu đơn qua `orderService.update`
   * (orders-aggregate.service.ts), endpoint CHƯA có wiring flash sale (không
   * tạo lại `FlashSaleReservation`, không trừ `remaining_quantity`, không
   * kiểm tra `max_per_customer`) — sửa tự do SL/giá của một dòng flash sale
   * ĐÃ LƯU ở đó là oversell thật (đơn tạo ở POS, nơi flash sale ĐƯỢC wiring
   * đầy đủ, mở lên sửa lại là lách được toàn bộ enforcement). Mặc định
   * `false` (không truyền) để giữ NGUYÊN hành vi POS — dòng flash sale ở đó
   * hoàn toàn hợp lệ để sửa SL/giá, BE (`promotion-engine.service.ts`) verify
   * lại `max_per_customer`/tồn kho lúc tạo đơn thật. Orders/edit.tsx truyền
   * `true`.
   */
  lockFlashSaleLines?: boolean;
}

/**
 * TCR-2475 — Ô "Tồn" cạnh SL (CHỈ KH tổ chức / B2B).
 *
 * Lý do có: ở đơn SỈ, thu ngân cần biết tồn của KHO ĐÃ CHỌN ngay tại dòng
 * hàng — trước đây phải mở modal "Chọn kho xuất hàng" mới thấy, tức là phải
 * đoán/mở đoán mỗi dòng. Số hiển thị là số BÁN ĐƯỢC (`sellable_available`,
 * fallback `available`) — đúng bằng con số BE dùng để chặn ở bước chọn kho,
 * và cùng nguồn với `WarehousePickerDialog` nên không thể có chuyện "màn này
 * báo còn hàng, màn kia báo hết".
 *
 * Nguồn: `posService.listVariantWarehouseStocks(variantId, storeId)` — CÙNG
 * API/cùng cách lấy `storeId` như `WarehousePickerDialog.tsx` (store của ca
 * POS khi màn không truyền `storeId`).
 *
 * Query key khoá theo `variantId` + store; KHO ĐÃ CHỌN không nằm trong key mà
 * được so khớp LÚC RENDER ⇒ đổi kho là số tự đổi theo ngay (đúng yêu cầu
 * "đổi kho xong cột Tồn phải cập nhật"), không phải chờ fetch lại.
 *
 * Trạng thái hiển thị:
 * - chưa chọn kho → "—" + tooltip "Chọn kho để xem tồn";
 * - đang tải → "…";
 * - lỗi mạng → "—";
 * - SL vượt tồn → số tồn tô đỏ (cảnh báo "Vượt tồn kho" dưới ô SL giữ nguyên).
 */
function StockCell({
  variantId,
  warehouseId,
  quantity,
  storeId,
  testId,
}: {
  variantId?: string;
  warehouseId?: string;
  /** SL của dòng — dùng để tô đỏ khi vượt tồn. */
  quantity: number;
  storeId?: string;
  testId: string;
}) {
  // Store của ca POS khi màn không truyền `storeId` — cùng cách lấy storeId như
  // `WarehousePickerDialog` (xem file đó): store đưa vào QUERY KEY để đổi
  // chi nhánh là cache cũ bị loại, không phải danh sách kho của chi nhánh cũ.
  const shiftStoreId = usePosShiftStoreId();
  const effectiveStoreId = storeId ?? shiftStoreId ?? undefined;

  const { data, isError } = useQuery<PosVariantWarehouseStocksResponse>({
    queryKey: ['pos', 'variant-warehouse-stocks', variantId ?? '', effectiveStoreId],
    queryFn: () => posService.listVariantWarehouseStocks(variantId ?? '', storeId),
    // Chỉ tra tồn khi dòng ĐÃ có kho — chưa chọn kho thì không có số nào để hiện.
    enabled: Boolean(variantId && warehouseId),
    staleTime: 30_000,
    retry: false,
  });

  // So khớp kho ĐANG CHỌN trong danh sách kho của variant.
  const rows = data?.items ?? [];
  const matched = rows.find((w) => String(w.warehouse_id) === String(warehouseId));
  const sellable = matched?.sellable_available ?? matched?.available;

  let content: React.ReactNode;
  let tooltip: string | undefined;
  if (!warehouseId) {
    content = '—';
    tooltip = 'Chọn kho để xem tồn';
  } else if (!variantId) {
    content = '—';
    tooltip = 'Chưa có biến thể để tra tồn';
  } else if (isError) {
    content = '—';
    tooltip = 'Không tải được tồn kho';
  } else if (!data) {
    content = '…';
  } else if (sellable === undefined) {
    content = '—';
    tooltip = 'Kho đã chọn không có trong danh sách tồn';
  } else {
    content = String(sellable);
    tooltip = `Số bán được tại kho đã chọn: ${sellable}`;
  }

  // SL vượt tồn → tô đỏ số tồn (để thu ngân thấy ngay dòng nào lấy không đủ).
  const isOverStock = typeof sellable === 'number' && quantity > sellable;

  return (
    <Tooltip title={tooltip}>
      <span
        data-testid={testId}
        style={{
          fontSize: 13,
          fontWeight: 600,
          textAlign: 'center',
          ...(isOverStock ? { color: '#ff4d4f' } : {}),
        }}
      >
        {content}
      </span>
    </Tooltip>
  );
}

function TableHeader({
  showWarehouseCol,
  showSellerCol,
  showSerialCol,
  showWeightCol,
  isB2B,
  qtyColWidth,
  totalColWidth,
}: {
  showWarehouseCol: boolean;
  showSellerCol: boolean;
  showSerialCol: boolean;
  showWeightCol: boolean;
  isB2B: boolean;
  /** Bề rộng động — xem `computeDynamicColWidths`, phải khớp `OrderItemRow`/`ComboGroupRow`. */
  qtyColWidth: string;
  totalColWidth: string;
}) {
  const th: React.CSSProperties = {
    padding: '8px 6px',
    fontSize: 13,
    fontWeight: 700,
    color: '#333',
    background: '#fafafa',
    borderBottom: '1px solid #e8e8e8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 0,
  };

  return (
    <div
      className="pos-order-table-header"
      style={{ display: 'flex', borderBottom: '1px solid #e8e8e8', background: '#fafafa', flexShrink: 0 }}
    >
      <div style={{ ...th, width: INDEX_COL_WIDTH, textAlign: 'center' }}>#</div>
      {/* TCR-967: bỏ chữ "TT" (Thao tác) vì dễ nhầm với "Thanh toán". Giữ nguyên ô
          header rỗng để header còn khớp cột nút xoá ở mỗi dòng bên dưới.
          TCR-2470: cột này đứng ngay sau STT (trước đây ở cuối dòng). */}
      <div style={{ ...th, width: DELETE_COL_WIDTH, textAlign: 'center' }} aria-label="Thao tác" />
      <div style={{ ...th, flex: 1, minWidth: 160 }}>Tên Hàng</div>
      {showSerialCol && <div style={{ ...th, width: 88, textAlign: 'center' }}>Mã QC</div>}
      <div style={{ ...th, width: PRICE_COL_WIDTH, textAlign: 'right' }}>{isB2B ? 'Giá áp dụng' : 'Giá Tiền'}</div>
      <div style={{ ...th, width: qtyColWidth, textAlign: 'center' }}>SL</div>
      {/* TCR-2475: KH tổ chức thêm cột "Tồn" ngay SAU cột SL — tồn bán được
          của kho đã chọn, thu ngân thấy ngay cạnh số lượng. */}
      {isB2B && <div style={{ ...th, width: STOCK_COL_WIDTH, textAlign: 'center' }}>Tồn</div>}
      {/* TCR-2125: KH tổ chức thêm cột Giá gợi ý, KHÔNG thay mất Loại + Chiết khấu. */}
      {isB2B && <div style={{ ...th, width: SUGGEST_COL_WIDTH, textAlign: 'right' }}>Giá gợi ý</div>}
      {/* Nhãn ngắn "Loại" (không phải "Loại CK") — 58px không đủ chỗ cho
          "Loại CK" ở fontWeight 700, tràn chữ "Loại..." trên header. */}
      <div style={{ ...th, width: DISCOUNT_TYPE_COL_WIDTH, textAlign: 'center' }}>Loại</div>
      <div className="pos-col-discount-percent" style={{ ...th, width: DISCOUNT_COL_WIDTH, textAlign: 'center' }}>Chiết khấu</div>
      <div style={{ ...th, width: totalColWidth, textAlign: 'right' }}>T.Tiền</div>
      {showWeightCol && (
        <div className="pos-col-weight" style={{ ...th, width: 52, textAlign: 'center' }}>TL</div>
      )}
      <div style={{ ...th, width: VAT_COL_WIDTH, textAlign: 'center' }}>VAT</div>
      {showSellerCol && <div style={{ ...th, width: 128, textAlign: 'center' }}>N.Bán</div>}
      {showWarehouseCol && <div style={{ ...th, width: 128, textAlign: 'center' }}>C.Kho</div>}
    </div>
  );
}

function OrderItemRow({
  item,
  lineVatById,
  lineTotalById,
  index,
  idPrefix = '',
  fulfillmentMode,
  showWarehouseCol,
  showSellerCol,
  showSerialCol,
  showWeightCol,
  storeId,
  stockStoreId,
  isStale,
  previewPrice,
  error,
  isB2B,
  discountPolicyEnabled,
  currentUserName,
  hasDiscountPermission,
  lockFlashSaleLines,
  qtyColWidth,
  totalColWidth,
  onUpdate,
  onRemove,
  onSplit,
  siblingQty = 0,
  onDropFlash,
  onRebalanceFlashGroup,
  maxQuantity,
  onEditSerial,
  onPickWarehouse,
}: {
  item: OrderItem;
  index: number;
  /** Tiền tố cho data-testid VÀ cho `id` của dòng — bảng desktop và danh sách
   *  mobile cùng render trong DOM (chỉ khác ẩn/hiện qua CSS), trùng testid sẽ
   *  vỡ getByTestId và trùng `id` là HTML sai (getElementById chỉ trả về cái
   *  đầu tiên, có thể đang bị ẩn). Cùng quy ước với `ComboGroupRow.idPrefix`. */
  idPrefix?: string;
  fulfillmentMode: FulfillmentMode;
  showWarehouseCol: boolean;
  showSellerCol: boolean;
  showSerialCol: boolean;
  showWeightCol: boolean;
  storeId?: string;
  /**
   * TCR-2475 — store dùng để tra TỒN của kho đã chọn (cột "Tồn", chỉ B2B).
   * Lấy từ store của ĐƠN (màn sửa đơn) nếu có, không thì store của ca POS.
   */
  stockStoreId?: string;
  isStale: boolean;
  previewPrice?: number;
  error?: PosItemError;
  isB2B: boolean;
  discountPolicyEnabled?: boolean;
  currentUserName?: string;
  hasDiscountPermission: boolean;
  /**
   * TCR-2058 — VAT từng dòng SAU khi phân bổ voucher/KM/điểm (cùng số BE lưu và
   * lên HĐĐT), khoá theo item id. `itemVatAmount` chỉ là VAT TRƯỚC phân bổ nên
   * chỉ làm fallback khi không truyền / thiếu id.
   */
  lineVatById?: ReadonlyMap<string, number>;
  /**
   * TCR-2224 — tiền cả dòng đã gồm VAT, làm tròn về đồng (`line_total`): tổng khách trả
   * = Σ số này. Khoá theo item id. Không truyền / thiếu id ⇒ không hiện (đơn cũ).
   */
  lineTotalById?: ReadonlyMap<string, number>;
  /** TCR-1143 — xem comment đầy đủ ở `Props.lockFlashSaleLines` (component cha). */
  lockFlashSaleLines?: boolean;
  /** Bỏ giá flash của chính dòng này. Không truyền ⇒ không hiện nút. */
  onDropFlash?: () => void;
  /** TCR-1423: SL đổi trên dòng thuộc nhóm flash sale (dòng flash gốc hoặc
   *  dòng dư) — phân bổ lại suất KM cho cả nhóm thay vì cấp giá flash cho
   *  toàn bộ SL. Không truyền ⇒ giữ hành vi cũ (kẹp theo `maxQuantity`). */
  onRebalanceFlashGroup?: (requestedQuantity: number) => void;
  /** Trần số lượng của chính dòng này, `undefined` = không khoá. */
  maxQuantity?: number;
  /** Bề rộng động — xem `computeDynamicColWidths`, phải khớp `TableHeader`/`ComboGroupRow`. */
  qtyColWidth: string;
  totalColWidth: string;
  onUpdate: (patch: Partial<OrderItem>) => void;
  onRemove: () => void;
  /** TCR-2614: tách dòng — undefined ⇒ không hiện nút. */
  onSplit?: () => void;
  /** TCR-2614: tổng SL các dòng khác cùng SKU + kho — cộng vào so sánh tồn kho. */
  siblingQty?: number;
  onEditSerial: () => void;
  onPickWarehouse: () => void;
}) {
  const isQuantityMode =
    item.trackingMode === 'quantity' || fulfillmentMode === 'PREORDER';
  // TCR-1143 (final review, finding #1): dòng bị KHOÁ sửa SL/giá/CK khi là
  // quà tặng BXGY (hành vi cũ, mọi trang) HOẶC khi là dòng flash sale VÀ
  // trang cha yêu cầu khoá qua `lockFlashSaleLines` (chỉ Orders/edit.tsx —
  // xem comment đầy đủ ở khai báo prop). POS KHÔNG truyền prop này nên dòng
  // flash sale ở POS vẫn sửa được bình thường, giữ nguyên hành vi cũ.
  // Dòng flash sale nhận diện qua HAI field: `flash_sale_item_id` (giá áp ngay
  // lúc dựng dòng — Plan 3 Task 2) và `flash_sale_id` (đường cũ, do endpoint
  // evaluate gắn — TCR-1143). Chỉ kiểm một cái là bỏ sót một nửa số dòng.
  const isFlashLine = Boolean(item.flash_sale_item_id || item.flash_sale_id);
  const isLineLocked = Boolean(item.is_gift) || Boolean(lockFlashSaleLines && isFlashLine);
  const isSlEditable = isQuantityMode && !isLineLocked;

  /**
   * TCR-1517 — COMBO THẬT. Combo thật LÀ một sản phẩm (SKU/biến thể/tồn kho
   * riêng) nên nó là dòng giỏ BÌNH THƯỜNG này: sửa số lượng / đơn giá / chiết
   * khấu y hệt mọi dòng khác. `bom_components` chỉ là snapshot định mức để
   * XEM bên trong có gì — không sinh dòng, không đổi tiền, không tách ra được
   * (nên cũng không có gì để chặn). Đừng lẫn với `ComboGroupRow` phía dưới:
   * đó là combo ẢO, nhiều dòng giỏ gom lại.
   */
  const bomComponents = item.bom_components ?? [];
  const canExpandBom = bomComponents.length > 0;
  const [bomExpanded, setBomExpanded] = useState(false);

  // Editing state per field — null khi không focus (hiển thị giá trị committed
  // đã format); string khi user đang gõ (hiển thị raw input value). Pattern này
  // copy từ POS legacy UI để hỗ trợ real-time validation + thousand separator.
  const [editPrice, setEditPrice] = useState<string | null>(null);
  const [editCkPct, setEditCkPct] = useState<string | null>(null);
  const [editCkAmt, setEditCkAmt] = useState<string | null>(null);
  const [editVat, setEditVat] = useState<string | null>(null);
  const [isVatDirty, setIsVatDirty] = useState(false);
  const [hasInvalidVatInput, setHasInvalidVatInput] = useState(false);
  const [editQty, setEditQty] = useState<string | null>(null);
  /**
   * TCR-1417: trước bản vá này, gõ SL vượt tồn kho (vd `999999999`) không có
   * cảnh báo nào tại ô nhập — Tổng tiền hàng nhảy lên số vô lý (hàng trăm
   * nghìn tỷ), người dùng chỉ phát hiện được khi bấm "Chọn kho" ở bước cuối
   * và thấy "Cần X sản phẩm"/"Bán được: 0". Đơn vẫn KHÔNG tạo sai được (chặn
   * thật vẫn ở bước chọn kho — không kho nào đủ tồn), đây chỉ là lớp cảnh
   * báo UX SỚM HƠN, không phải validation chặn cứng — tồn kho đổi liên tục
   * nên số cảnh báo ở đây có thể lệch nhẹ so với lúc chọn kho thật.
   */
  const [stockWarning, setStockWarning] = useState<string | null>(null);

  const td: React.CSSProperties = {
    padding: '8px 4px',
    fontSize: 13,
    borderBottom: '1px solid #f5f5f5',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  // ---- Price ----
  const priceDisplay = editPrice !== null ? editPrice : formatUnitPrice(item.price);

  function handlePriceFocus() {
    setEditPrice(formatUnitPrice(item.price));
  }
  function handlePriceChange(raw: string) {
    setEditPrice(formatUnitPriceInput(raw));
  }
  function handlePriceBlur() {
    // TCR-2224 — đơn giá nhận tối đa 2 chữ số lẻ (dấu phẩy). `parsePrice` cũ chỉ giữ
    // chữ số nên giá lẻ bị nhân ×100 ngay khi focus rồi rời ô.
    const parsed = parseUnitPrice(editPrice ?? '');
    setEditPrice(null);
    // `parseUnitPrice` chỉ giữ digit/dấu phẩy nên không bao giờ ra số âm — guard trước đây
    // `parsed <= 0` vô tình chặn luôn 0đ, gõ 0 rồi rời ô lại thấy giá cũ (revert
    // câm, không có thông báo gì). Combo đã cho 0đ từ trước (issue "combo giá
    // 0đ") — sản phẩm thường phải nhất quán, cùng cho sửa về 0đ.
    if (parsed === item.price) return;
    // TCR-2425 — công thức (và 2 mốc so sánh `hasPriceOverride` của B2B/bán lẻ)
    // nằm trong `applyLinePrice` để tính năng "sao chép dữ liệu cho các dòng
    // bên dưới" ra ĐÚNG cùng một số với thao tác tay. Xem `lineValueCalc.ts`:
    //   - B2B: mốc là giá gợi ý (`suggestedPrice ?? listPrice`), giữ CK cấp giá;
    //   - bán lẻ: mốc là giá lẻ gốc (`retailPrice ?? listPrice`) — `comboMatcher`
    //     loại dòng `hasPriceOverride` khỏi gợi ý gộp (spec §3.4), nên cờ phải
    //     được ghi ở CẢ nhánh thường, không chỉ B2B.
    const next = applyLinePrice(item, parsed, fulfillmentMode, isB2B);
    if (isB2B) {
      onUpdate({
        price: next.price,
        discountType: next.discountType,
        discountPercent: next.discountPercent,
        discountAmount: next.discountAmount,
        totalAmount: next.totalAmount,
        hasPriceOverride: next.hasPriceOverride,
      });
      return;
    }
    onUpdate({
      price: next.price,
      discountAmount: next.discountAmount,
      totalAmount: next.totalAmount,
      hasPriceOverride: next.hasPriceOverride,
    });
  }

  // ---- CK% ----
  // TCR-2129 — nhãn CS + trạng thái lệch chính sách (chỉ dòng thường, khi kênh bật chính sách).
  const ckPolicyOn = !!discountPolicyEnabled && !item.comboGroupId && item.policyDiscount != null;
  const ckDeviating = ckPolicyOn && isDiscountDeviating(item, fulfillmentMode);
  const ckDeviation = ckDeviating ? getDeviationAmount(item, fulfillmentMode) : 0;
  const ckDeviationTooltip = ckDeviating ? (
    <div>
      <div>CK chính sách: {formatPolicyValue(item.policyDiscount)}</div>
      <div>
        CK sau sửa: {item.discountType === 'amount' ? formatVND(item.discountAmount) : `${item.discountPercent}%`}{' '}
        ({ckDeviation > 0 ? '+' : ''}{formatVND(ckDeviation)})
      </div>
      <div>Lý do: {item.overrideReason?.trim() || 'Chưa nhập'}</div>
      <div>Người sửa: {item.discountDeviationBy || currentUserName || '—'}</div>
    </div>
  ) : null;
  // TCR-2129 — CK thực hiển thị trên dải lý do: % hoặc tiền/SP (discountAmount ở chế độ SL là cả dòng).
  const ckActualLabel =
    item.discountType === 'amount'
      ? formatVND(isQuantityMode && item.quantity > 0 ? item.discountAmount / item.quantity : item.discountAmount)
      : `${item.discountPercent}%`;
  const ckPctDisplay = editCkPct !== null ? editCkPct : String(item.discountPercent);
  // TCR-1154 — loại chiết khấu (% hay ₫). Chỉ là tuỳ chọn HIỂN THỊ/THAO TÁC,
  // không đổi cách lưu (discountPercent + discountAmount luôn được giữ đồng bộ).
  const discountType: 'percent' | 'amount' = item.discountType ?? 'percent';
  function handleDiscountTypeChange(type: 'percent' | 'amount') {
    if (type === discountType) return;
    onUpdate({ discountType: type });
  }

  function handleCkPctFocus() {
    setEditCkPct(String(item.discountPercent));
  }
  function handleCkPctChange(raw: string) {
    // Real-time guard: cho phép rỗng hoặc digit + 1 dấu chấm. Reject nếu vượt 100.
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    const num = parseFloat(raw);
    if (raw !== '' && !Number.isNaN(num) && num > 100) return;
    setEditCkPct(raw);
  }
  function handleCkPctBlur() {
    let parsed = parseDecimal(editCkPct ?? '');
    parsed = Math.min(100, Math.max(0, parsed));
    setEditCkPct(null);
    if (parsed === item.discountPercent) return;
    // TCR-2425 — dùng chung `applyLineDiscountPercent` với tính năng sao chép.
    const next = applyLineDiscountPercent(item, parsed, fulfillmentMode);
    onUpdate({
      discountPercent: parsed,
      discountAmount: next.discountAmount,
      totalAmount: next.totalAmount,
      hasDiscountOverride: true,
    });
  }

  // ---- CK amount ----
  const ckAmtDisplay =
    editCkAmt !== null ? editCkAmt : formatPrice(item.discountAmount);

  function handleCkAmtFocus() {
    // Mirror Price column: hiển thị giá trị đã format ngay khi focus + giữ
    // thousand separator trong lúc gõ + sau khi blur (display fall back về
    // formatPrice(item.discountAmount)). Trước đây focus set raw "2000000"
    // nên user thấy mất dấu chấm phân cách — EG-1757.
    setEditCkAmt(formatPrice(item.discountAmount));
  }
  function handleCkAmtChange(raw: string) {
    setEditCkAmt(formatPriceInput(raw));
  }
  function handleCkAmtBlur() {
    const parsedAmount = parsePrice(editCkAmt ?? '');
    setEditCkAmt(null);
    // TCR-2224 — thành tiền làm tròn về đồng; CK không vượt thành tiền.
    const maxDiscount = itemGrossAmount(item.price, item.quantity, isQuantityMode);
    const clampedAmount = Math.min(Math.max(Math.round(parsedAmount), 0), maxDiscount);
    if (clampedAmount === item.discountAmount) return;
    // Ô CK tiền nhập TỔNG tiền CK của dòng, còn `applyLineDiscountAmountPerUnit`
    // (dùng chung với sao chép) nhận tiền CK MỖI ĐƠN VỊ — quy đổi ở đây.
    const perUnitAmount = isQuantityMode
      ? item.quantity > 0
        ? clampedAmount / item.quantity
        : 0
      : clampedAmount;
    const next = applyLineDiscountAmountPerUnit(item, perUnitAmount, fulfillmentMode);
    onUpdate({
      discountPercent: next.discountPercent,
      discountAmount: next.discountAmount,
      totalAmount: next.totalAmount,
      hasDiscountOverride: true,
    });
  }

  // ---- VAT ----
  const vatDisplay = editVat !== null ? editVat : formatVatRate(item.vat);

  function handleVatFocus() {
    setEditVat(formatVatRate(item.vat));
    setIsVatDirty(false);
    setHasInvalidVatInput(false);
  }
  function handleVatChange(raw: string) {
    setIsVatDirty(true);
    if (raw !== '' && !/^\d*\.?\d{0,2}$/.test(raw)) {
      // Ignore extra fractional digits so the last accepted value remains
      // editable/committable instead of turning a valid value into zero.
      if (!/^\d*\.\d{3,}$/.test(raw)) setHasInvalidVatInput(true);
      return;
    }
    const parsed = Number(raw);
    if (raw !== '' && !Number.isNaN(parsed) && parsed > 100) {
      setHasInvalidVatInput(true);
      return;
    }
    setHasInvalidVatInput(false);
    setEditVat(raw);
  }
  function handleVatBlur() {
    const parsed = parseVatInput(editVat ?? '');
    const shouldCommitZero = hasInvalidVatInput || parsed === null;
    setEditVat(null);
    if (!isVatDirty) return;
    setIsVatDirty(false);
    setHasInvalidVatInput(false);
    const normalized = shouldCommitZero ? 0 : normalizeVatRate(parsed);
    onUpdate({ vat: normalized, vatOverride: normalized, hasVatOverride: true });
  }

  // ---- Quantity ----
  const qtyDisplay = editQty !== null ? editQty : String(item.quantity);

  function handleQtyFocus() {
    setEditQty(String(item.quantity));
    setStockWarning(null);
  }
  function handleQtyChange(raw: string) {
    if (raw !== '' && !/^\d*$/.test(raw)) return;
    setEditQty(raw);
    setStockWarning(null);
  }
  /**
   * TCR-1417: kiểm tồn NGAY khi thu ngân rời ô SL — cùng thời điểm/API với
   * `lookupVariants` mà luồng combo growth đã dùng (xem `growComboGroup`
   * trong `index.tsx`), để badge tồn ở màn sản phẩm và cảnh báo ở đây luôn
   * cùng một nguồn số. Chỉ cảnh báo (không kẹp/chặn số đã nhập) — chặn thật
   * vẫn ở bước chọn kho vì tồn kho đổi liên tục, số ở đây có thể đã cũ vài
   * giây sau khi fetch.
   */
  function checkStockWarning(quantity: number) {
    if (fulfillmentMode === 'PREORDER' || !item.variantCode) return;
    posService
      .lookupVariants([item.variantCode], fulfillmentMode)
      .then((res) => {
        const stock = res.items.find(
          (i) => i.variant.variant_id === item.variantId,
        )?.stock_quantity;
        // TCR-2614: 2 dòng cùng SKU cùng trừ một kho — so TỔNG SL các dòng với tồn.
        const total = quantity + siblingQty;
        if (typeof stock === 'number' && total > stock) {
          const text = `Chỉ còn ${stock} sản phẩm trong kho — số lượng đã nhập (${total}) vượt tồn`;
          setStockWarning(text);
          void message.warning(`${item.variantName || item.variantCode}: ${text}`);
        }
      })
      .catch(() => {
        // Cảnh báo sớm là best-effort — lỗi tra tồn ở đây không được chặn
        // luồng bán hàng, chặn thật vẫn còn nguyên ở bước chọn kho.
      });
  }
  function handleQtyBlur() {
    const parsed = parseInt(editQty ?? '', 10);
    // Kẹp ở `maxQuantity` (nếu có) chứ không chỉ chặn ở UI: gõ tay rồi blur là
    // đường đi thẳng nhất qua mọi cái disabled.
    const raw = Number.isNaN(parsed) ? 1 : Math.max(1, parsed);
    const valid = typeof maxQuantity === 'number' ? Math.min(raw, maxQuantity) : raw;
    setEditQty(null);
    if (valid === item.quantity) return;
    // TCR-1417: cảnh báo tồn phải chạy TRƯỚC nhánh flash bên dưới — nhánh đó
    // `return` sớm, đặt sau thì dòng flash sale mất hẳn cảnh báo vượt tồn.
    checkStockWarning(valid);
    // TCR-1423: dòng thuộc nhóm flash sale (dòng flash gốc — nhận diện qua
    // `flashRemaining` — HOẶC dòng dư đã tách ra trước đó — `flashOverflowOf`)
    // luôn phải phân bổ lại suất KM cho CẢ NHÓM khi SL đổi, dù TĂNG hay GIẢM:
    // giảm SL ở dòng flash có thể kéo dòng dư gộp lại về giá KM (bug QA:
    // `splitFlashOverflow` cũ (đã xoá) chỉ tách một chiều, giảm SL không gộp lại được).
    // Xem `rebalanceFlashGroup`.
    if (
      onRebalanceFlashGroup &&
      (typeof item.flashRemaining === 'number' || item.flashOverflowOf)
    ) {
      onRebalanceFlashGroup(valid);
      return;
    }
    // TCR-2129: CK dạng VND giữ NGUYÊN số tiền mỗi SP khi đổi SL. Tính lại từ % (đã làm tròn
    // 2 chữ số) sẽ lệch vài đồng so với CS ⇒ báo "Sửa CK" giả dù NVKD không đụng tới CK.
    //
    // TCR-2425 — cả 2 nhánh (CK tiền giữ số tiền/đv, còn lại recalc) đã nằm trong
    // `applyLineQuantity`, dùng chung với tính năng sao chép SL cho các dòng dưới.
    const isAmountDiscount = item.discountType === 'amount' && item.quantity > 0;
    const next = applyLineQuantity(item, valid, fulfillmentMode);
    onUpdate({
      quantity: valid,
      discountAmount: next.discountAmount,
      totalAmount: next.totalAmount,
      // Nhánh CK tiền phải ghi lại % quy đổi (nhánh recalc giữ nguyên % cũ) —
      // giữ đúng hình dạng patch trước đây để không đụng hành vi cũ.
      ...(isAmountDiscount ? { discountPercent: next.discountPercent } : {}),
    });
  }

  return (
    <>
    <div id={`${idPrefix}pos-item-row-${item.id}`} className="pos-order-row" style={{ display: 'flex', borderBottom: '1px solid #f5f5f5' }}>
      {/* STT — khối 2 dòng (số + dòng ẩn) như Giá Tiền/VAT/SL/Loại CK/Chiết
          khấu/T.Tiền: căn giữa NHƯNG mọi ô đều "cao" như nhau (dòng phụ dù
          rỗng vẫn chiếm chỗ) nên căn giữa không làm số 1-dòng lệch khỏi ô
          2-dòng có chú thích thật. Không dùng `justifyContent: 'flex-start'`
          — ghim thẳng lên đỉnh: đúng khi MỌI ô cùng cao 2 dòng, nhưng lệch
          ngay khi có ô khác cao hơn (vd Giá Tiền B2B có thêm dòng tag). */}
      <div style={{ ...td, width: INDEX_COL_WIDTH, flexDirection: 'column', justifyContent: 'center', gap: 2, color: '#999' }}>
        <span>{index + 1}</span>
        <Text type="secondary" style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.1 }}>
          {' '}
        </Text>
      </div>

      {/* TT - Delete — TCR-2470: ngay sau STT, không phải cuộn ngang tới cuối dòng */}
      <div style={{ ...td, width: DELETE_COL_WIDTH, justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined style={{ fontSize: 14 }} />}
          onClick={onRemove}
        />
        {/* TCR-2614 — Tách dòng: thêm dòng cùng mã để đặt giá/số lượng khác (vd hàng tặng giá 0).
            Chỉ dòng lẻ thường; không hiện ở combo/flash sale/serial/quà/dòng đã tách. */}
        {onSplit && canSplitLine(item) && !isLineLocked && (
          <Tooltip title="Thêm dòng cùng mã để đặt giá/số lượng khác, vd hàng tặng">
            <Button
              type="text"
              size="small"
              data-testid={`${idPrefix}pos-item-split-${item.id}`}
              aria-label="Tách dòng"
              icon={<SplitCellsOutlined style={{ fontSize: 14 }} />}
              onClick={onSplit}
            />
          </Tooltip>
        )}
        {item.splitLine && (
          <Tooltip title="Dòng tách từ cùng mã hàng — đặt giá/số lượng riêng">
            <Tag color="gold" style={{ margin: 0, padding: '0 2px', fontSize: 10, lineHeight: '14px' }}>Tách</Tag>
          </Tooltip>
        )}
      </div>

      {/* Tên Hàng */}
      <div style={{ ...td, flex: 1, minWidth: 160, alignItems: 'flex-start', gap: 8 }}>
        {/* TCR-1517 — mũi tên bung thành phần combo THẬT, đứng TRƯỚC ảnh sản
            phẩm đúng như cụm combo ẢO. `paddingTop` để nút 18px nằm ngang giữa
            ảnh 40px thay vì dính mép trên (cột này `alignItems: flex-start` vì
            tên sản phẩm có thể dài nhiều dòng). */}
        {canExpandBom && (
          <div style={{ flex: 'none', paddingTop: 10 }}>
            <ComboExpandToggle
              expanded={bomExpanded}
              onToggle={() => setBomExpanded((prev) => !prev)}
              testId={`${idPrefix}pos-combo-components-toggle-${item.id}`}
            />
          </div>
        )}
        <Image
          src={item.productImage || ''}
          alt={item.variantName}
          width={40}
          height={40}
          preview={false}
          style={{
            objectFit: 'cover',
            borderRadius: 4,
            flexShrink: 0,
            backgroundColor: '#f5f5f5',
            border: '1px solid #d9d9d9',
          }}
          fallback={POS_IMAGE_FALLBACK}
        />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <Text style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'normal', wordBreak: 'break-word' }}>
              {item.variantName}
            </Text>
            {/* TCR-1517 — nhãn + nút mở danh sách thành phần của combo THẬT.
                Thu ngân phải nhìn ra dòng này là combo và bung được ra xem
                bên trong có gì; bảng bung ra là CHỈ XEM. */}
            {item.is_combo_product && (
              <Tag
                color="#a4193d"
                data-testid={`${idPrefix}pos-combo-product-tag-${item.id}`}
                style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
              >
                Combo
              </Tag>
            )}
            {isFlashLine && (
              <>
                <Tag
                  icon={<ThunderboltOutlined />}
                  color="red"
                  data-testid="pos-flash-tag"
                  style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                >
                  Flash
                </Tag>
                {/* Giá gạch ngang: không có nó thì thu ngân không còn cách nào
                    biết chương trình vừa giảm bao nhiêu cho dòng này. */}
                {typeof item.retailPrice === 'number' && item.retailPrice > item.price && (
                  <Text
                    delete
                    data-testid="pos-flash-original-price"
                    style={{ fontSize: 11, color: '#888' }}
                  >
                    {formatVND(item.retailPrice)}
                  </Text>
                )}
                {typeof item.flashRemaining === 'number' && (
                  <Text data-testid="pos-flash-remaining" style={{ fontSize: 11, color: '#888' }}>
                    còn {item.flashRemaining}
                  </Text>
                )}
                {onDropFlash && !isLineLocked && (
                  <Button
                    size="small"
                    type="text"
                    data-testid="pos-flash-drop-btn"
                    aria-label="Bỏ giá flash sale"
                    style={{ padding: '0 4px', height: 18, fontSize: 11, color: '#888' }}
                    onClick={onDropFlash}
                  >
                    ✕
                  </Button>
                )}
              </>
            )}
            {item.is_gift && (
              <Tag icon={<GiftOutlined />} color="green" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                Quà tặng
              </Tag>
            )}
          </div>
          <Text style={{ fontSize: 11, color: '#1677ff', whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {item.variantCode}
          </Text>
          {item.variantDisplayName && (
            <Text style={{ fontSize: 11, color: '#888', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              Tên biến thể: {item.variantDisplayName}
            </Text>
          )}
          {canPickWarehouse(item, fulfillmentMode) && (
            <>
              <Button
                type="link"
                size="small"
                danger={error?.warehousePick}
                style={{ padding: 0, height: 'auto', fontSize: 11 }}
                onClick={onPickWarehouse}
              >
                📦 {item.warehouseName ?? 'Chọn kho'}
              </Button>
              {error?.warehousePick && (
                <Text style={{ fontSize: 11, color: '#ff4d4f' }}>Bắt buộc chọn kho xuất hàng</Text>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mã QC (serial) — bấm để đổi nếu dòng có serial */}
      {showSerialCol && (
        <div style={{ ...td, width: 88, justifyContent: 'center', fontSize: 12 }}>
          {item.specificationCode ? (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto', fontSize: 12 }}
              onClick={onEditSerial}
            >
              {item.specificationCode}
            </Button>
          ) : (
            <span style={{ color: '#bbb' }}>—</span>
          )}
        </div>
      )}

      {/* Giá Tiền / Giá áp dụng — luôn render Input (giống CK%/CK/SL). Preview giá mới hiển thị
          dưới input khi stale. */}
      <div
        style={{
          ...td,
          width: PRICE_COL_WIDTH,
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'center',
          gap: 2,
          lineHeight: 1.2,
        }}
      >
        {(() => {
          const showPreview =
            isStale && previewPrice !== undefined && previewPrice !== item.price;
          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                {isStale && (
                  <Tooltip
                    title={
                      previewPrice !== undefined
                        ? `Giá cũ ${formatPrice(item.price)} — giá mới ${formatPrice(previewPrice)}`
                        : 'Giá cũ — đã có giá mới từ thị trường'
                    }
                  >
                    <span style={{ color: '#ff4d4f', fontSize: 10, lineHeight: 1 }}>●</span>
                  </Tooltip>
                )}
                <Input
                  data-testid={`pos-item-price-${idPrefix}${item.id}`}
                  style={{ flex: 1, minWidth: 0, textAlign: 'right' }}
                  value={priceDisplay}
                  onFocus={handlePriceFocus}
                  onChange={(e) => handlePriceChange(e.target.value)}
                  onBlur={handlePriceBlur}
                  onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                  inputMode="decimal"
                  disabled={!hasDiscountPermission || isLineLocked}
                />
              </div>
              {/* TCR-1154 — hiển thị đơn giá đã gồm VAT (vd 120.000 + 10% → 132.000).
                  LUÔN mount (giống dòng số tiền VAT ở cột VAT bên dưới) — rỗng
                  khi không có VAT thay vì gỡ hẳn khỏi cây DOM, để khối
                  Input+chú thích của MỌI dòng cao bằng nhau. Ẩn hẳn dòng này
                  khi vat=0 làm khối co lại 1 dòng, `justifyContent: 'center'`
                  ở trên kéo Input lệch xuống so với các ô khác trong CÙNG
                  dòng (đã thấy lệch ~9-10px giữa Giá Tiền/SL khi không VAT). */}
              <Text type="secondary" style={{ fontSize: 11, textAlign: 'right', lineHeight: 1.1 }}>
                {(item.vat ?? 0) > 0 ? `${formatPrice(priceIncludingVat(item))}đ gồm VAT` : ' '}
              </Text>
              {showPreview && (
                <Tooltip title="Sẽ áp giá này khi bấm “Cập nhật giá” trên banner">
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: previewPrice > item.price ? '#cf1322' : '#389e0d',
                      textAlign: 'right',
                    }}
                  >
                    {formatPrice(previewPrice)}
                  </span>
                </Tooltip>
              )}
              {isB2B && item.hasPriceOverride && (
                <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <Tag color="orange" style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>
                    Sửa tay
                  </Tag>
                  {item.suggestedPrice != null && item.price !== item.suggestedPrice && item.suggestedPrice > 0 && (
                    <Tooltip title="Giá áp dụng lệch giá gợi ý">
                      <Text type="warning" style={{ fontSize: 11 }}>
                        {`${(((item.price - item.suggestedPrice) / item.suggestedPrice) * 100).toFixed(1)}%`}
                      </Text>
                    </Tooltip>
                  )}
                </div>
              )}
            </>
          );
        })()}
      </div>

      {/* SL — TCR-1154 dời lên sát Giá Tiền để nhóm "Số lượng – Loại CK – Chiết khấu" gần nhau.
          Dòng chú thích ẩn thứ 2 (giống Giá Tiền/VAT) để khối cao bằng các ô
          có chú thích thật — xem ghi chú tại ô Giá Tiền. */}
      <div style={{ ...td, width: qtyColWidth, flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        {isSlEditable ? (
          <Tooltip
            title={
              stockWarning ??
              (typeof maxQuantity === 'number' && maxQuantity <= item.quantity
                ? 'Chương trình flash sale đã kết thúc — giữ nguyên giá đã bán, không tăng thêm số lượng'
                : undefined)
            }
          >
            <Input
              style={{
                width: '100%',
                textAlign: 'center',
                padding: '4px 2px',
                ...(stockWarning ? { borderColor: '#ff4d4f' } : {}),
              }}
              status={stockWarning ? 'error' : undefined}
              value={qtyDisplay}
              data-testid={`${idPrefix}pos-item-qty-${item.id}`}
              onFocus={handleQtyFocus}
              onChange={(e) => handleQtyChange(e.target.value)}
              onBlur={handleQtyBlur}
              onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
              inputMode="numeric"
            />
          </Tooltip>
        ) : (
          <Text style={{ fontSize: 13, color: '#999', textAlign: 'center' }}>{item.quantity}</Text>
        )}
        <Text
          type={stockWarning ? undefined : 'secondary'}
          style={{
            fontSize: 11,
            textAlign: 'center',
            lineHeight: 1.1,
            ...(stockWarning ? { color: '#ff4d4f' } : {}),
          }}
        >
          {stockWarning ? 'Vượt tồn kho' : ' '}
        </Text>
      </div>

      {/* TCR-2475 — cột "Tồn" (chỉ B2B): số BÁN ĐƯỢC tại kho đã chọn, đứng
          ngay cạnh SL để thu ngân thấy ngay dòng nào lấy không đủ hàng. */}
      {isB2B && (
        <div style={{ ...td, width: STOCK_COL_WIDTH, flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
          <StockCell
            variantId={item.variantId}
            warehouseId={item.warehouseId}
            quantity={item.quantity + siblingQty}
            storeId={stockStoreId}
            testId={`${idPrefix}pos-item-stock-${item.id}`}
          />
          {/* Dòng chú thích ẩn thứ 2 — giữ cao ô bằng các ô 2 dòng khác
              trong cùng dòng (xem ghi chú tại ô Giá Tiền). */}
          <Text type="secondary" style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.1 }}>
            {' '}
          </Text>
        </div>
      )}

      {/* Giá gợi ý — chỉ B2B; Loại CK + Chiết khấu luôn có (TCR-2125) */}
      {isB2B && (
        <div
          style={{
            ...td,
            width: SUGGEST_COL_WIDTH,
            flexDirection: 'column',
            alignItems: 'flex-end',
            justifyContent: 'center',
            gap: 2,
            lineHeight: 1.2,
          }}
        >
          {item.suggestedPrice != null ? (
            <>
              <Text style={{ fontSize: 13, fontWeight: 600 }}>
                {formatPrice(item.suggestedPrice)}
              </Text>
              {(item.priceTierName || item.priceTier) ? (
                <Tag
                  color={item.priceTier === 'SPECIAL' ? 'purple' : 'blue'}
                  style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
                >
                  {item.priceTierName || item.priceTier}
                </Tag>
              ) : (
                <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.1 }}>{' '}</Text>
              )}
            </>
          ) : (
            <>
              <Tooltip title={item.brandId ? 'Chưa có CKTM — đang dùng giá bán lẻ' : 'SP chưa gắn thương hiệu — dùng giá bán lẻ'}>
                <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
              </Tooltip>
              <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.1 }}>{' '}</Text>
            </>
          )}
        </div>
      )}
      <div style={{ ...td, width: DISCOUNT_TYPE_COL_WIDTH, flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        <Select
          size="small"
          data-testid={`pos-item-discount-type-${idPrefix}${item.id}`}
          value={discountType}
          onChange={handleDiscountTypeChange}
          options={[
            { value: 'percent', label: '%' },
            { value: 'amount', label: '₫' },
          ]}
          disabled={!hasDiscountPermission || isLineLocked}
          style={{ width: '100%' }}
          aria-label="Loại chiết khấu"
        />
        <Text type="secondary" style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.1 }}>
          {' '}
        </Text>
      </div>

      <div className="pos-col-discount-percent" style={{ ...td, width: DISCOUNT_COL_WIDTH, flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        {/* TCR-2129 — CS (chính sách) + cảnh báo khi CK khác chính sách; chỉ dòng thường, bật theo kênh. */}
        <div
          style={
            ckDeviating
              ? { background: '#fff7e6', border: '1px solid #fa8c16', borderRadius: 6, display: 'flex', alignItems: 'center' }
              : undefined
          }
        >
            {discountType === 'percent' ? (
              <Input
                data-testid={`pos-item-ckpct-${idPrefix}${item.id}`}
                style={{ width: '100%', textAlign: 'center' }}
                value={ckPctDisplay}
                onFocus={handleCkPctFocus}
                onChange={(e) => handleCkPctChange(e.target.value)}
                onBlur={handleCkPctBlur}
                onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                inputMode="decimal"
                suffix="%"
                disabled={!hasDiscountPermission || isLineLocked}
              />
            ) : (
              <Input
                data-testid={`pos-item-ckamt-${idPrefix}${item.id}`}
                style={{ width: '100%', textAlign: 'right' }}
                value={ckAmtDisplay}
                onFocus={handleCkAmtFocus}
                onChange={(e) => handleCkAmtChange(e.target.value)}
                onBlur={handleCkAmtBlur}
                onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                inputMode="numeric"
                suffix="₫"
                disabled={!hasDiscountPermission || isLineLocked}
              />
            )}
          {ckDeviating && (
            <Tooltip title={ckDeviationTooltip}>
              <WarningOutlined data-testid="discount-deviation-warning" style={{ color: '#fa8c16', margin: '0 4px' }} />
            </Tooltip>
          )}
        </div>
        {ckPolicyOn ? (
          <span data-testid="discount-policy-label" style={{ fontSize: 11, color: '#8c8c8c', lineHeight: '14px', textAlign: 'center' }}>
            {formatPolicyLabel(item.policyDiscount)}
          </span>
        ) : (
          <Text type="secondary" style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.1 }}>
            {' '}
          </Text>
        )}
      </div>

      {/* T.Tiền — compact 2-line preview to match price column. Không preview
          thì vẫn có dòng chú thích ẩn thứ 2 để khối cao bằng Giá Tiền/VAT —
          nếu không, `justifyContent: 'center'` sẽ kéo số xuống GIỮA ô cao
          2 dòng thay vì đứng ngang hàng số ở các ô khác trong CÙNG dòng. */}
      <div
        style={{
          ...td,
          width: totalColWidth,
          padding: '8px 1px',
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'center',
          gap: 0,
          lineHeight: 1.2,
        }}
      >
        {(() => {
          const showPreview =
            isStale && previewPrice !== undefined && previewPrice !== item.price;
          if (!showPreview) {
            return (
              <>
                <span style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>
                  {formatPrice(item.totalAmount)}
                </span>
                {lineTotalById?.has(item.id) ? (
                  <Tooltip title={LINE_TOTAL_CAPTION_TITLE}>
                    <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.1 }}>
                      {lineTotalCaption(lineTotalById.get(item.id) ?? 0)}
                    </Text>
                  </Tooltip>
                ) : (
                  <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.1 }}>
                    {' '}
                  </Text>
                )}
              </>
            );
          }
          const newTotal = recalcItem({ ...item, price: previewPrice }, fulfillmentMode).totalAmount;
          return (
            <>
              <span style={{ fontSize: 11, textDecoration: 'line-through', color: '#999', whiteSpace: 'nowrap' }}>
                {formatPrice(item.totalAmount)}
              </span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  color: newTotal > item.totalAmount ? '#cf1322' : '#389e0d',
                }}
              >
                {formatPrice(newTotal)}
              </span>
            </>
          );
        })()}
      </div>

      {/* TL (weight) — dòng chú thích ẩn thứ 2 để khối cao bằng các ô khác. */}
      {showWeightCol && (
        <div
          className="pos-col-weight"
          style={{ ...td, width: 52, flexDirection: 'column', justifyContent: 'center', gap: 2, color: '#555', fontSize: 13 }}
        >
          <span>{item.weight != null ? item.weight.toFixed(2) : '—'}</span>
          <Text type="secondary" style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.1 }}>
            {' '}
          </Text>
        </div>
      )}

      {/* VAT — % + số tiền thuế (TCR-1154) */}
      <div style={{ ...td, width: VAT_COL_WIDTH, flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        <Input
          aria-label="VAT"
          style={{ width: '100%', textAlign: 'center' }}
          value={vatDisplay}
          onFocus={handleVatFocus}
          onChange={(e) => handleVatChange(e.target.value)}
          onBlur={handleVatBlur}
          onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
          inputMode="decimal"
          suffix="%"
        />
        <Text type="secondary" style={{ fontSize: 11, textAlign: 'center', lineHeight: 1.1 }}>
          {item.vat ? `${formatPrice(lineVatById?.get(item.id) ?? itemVatAmount(item))} đ` : ' '}
        </Text>
      </div>

      {/* N.Bán */}
      {showSellerCol && (
        <div style={{ ...td, width: 128 }}>
          <StaffAsyncSelect
            value={item.sellerStaffId}
            onChange={(staffId) => onUpdate({ sellerStaffId: staffId })}
            storeId={storeId}
            placeholder="NV"
            error={error?.seller}
            errorText="Bắt buộc chọn nhân viên bán"
          />
        </div>
      )}

      {/* C.Kho */}
      {showWarehouseCol && (
        <div style={{ ...td, width: 128 }}>
          <StaffAsyncSelect
            value={item.warehouseStaffId}
            onChange={(staffId) => onUpdate({ warehouseStaffId: staffId })}
            storeId={storeId}
            allowedStaffIds={item.warehouseStaffIds}
            placeholder="Kho"
            warning={item.warehouseStaffIds !== undefined && item.warehouseStaffIds.length === 0}
            error={error?.warehouse}
            errorText="Bắt buộc chọn nhân viên kho"
          />
        </div>
      )}
    </div>
    {/* TCR-2129 — dải lý do sửa CK full-width NGAY DƯỚI dòng hàng. Nằm NGOÀI
        `.pos-order-row` (flex ngang) để kéo hết bề ngang dòng, thay vì bị nhét
        trong cột CK ~90px làm cắt chữ. Mỗi layout (desktop/mobile) render
        `OrderItemRow` riêng nên mỗi layout có 1 dải; testid ô nhập giữ nguyên
        để index.tsx/edit.tsx focus khi chặn lưu thiếu lý do. */}
    {ckDeviating && (
      <div
        data-testid="discount-reason-strip"
        style={{
          background: '#fff7e6',
          border: '1px solid #ffd591',
          borderLeft: '3px solid #fa8c16',
          borderRadius: 6,
          padding: '6px 12px',
          margin: '4px 8px 8px',
        }}
      >
        <div style={{ fontSize: 12, color: '#ad4e00', marginBottom: 4 }}>
          <WarningOutlined style={{ color: '#fa8c16', marginRight: 6 }} />
          Lý do sửa CK (CS {formatPolicyValue(item.policyDiscount)} → {ckActualLabel},{' '}
          {ckDeviation > 0 ? '+' : ckDeviation < 0 ? '−' : ''}{formatVND(Math.abs(ckDeviation))}) *
        </div>
        <Input
          data-testid="discount-reason-input"
          style={{ width: '100%' }}
          placeholder="Nhập lý do sửa chiết khấu khác chính sách"
          status={item.overrideReason?.trim() ? undefined : 'warning'}
          value={item.overrideReason ?? ''}
          maxLength={500}
          onChange={(e) => onUpdate({ overrideReason: e.target.value })}
        />
      </div>
    )}
    {/* TCR-1517 — bảng thành phần combo THẬT, CHỈ XEM. Nằm NGOÀI
        `.pos-order-row` (dòng là flex ngang) để chiếm trọn bề ngang bên dưới
        dòng cha thay vì bị ép thành một "cột" nữa. Số lượng nhân theo SL combo
        trong giỏ nên thu ngân đọc được đúng lượng hàng sẽ xuất kho. */}
    {bomExpanded && canExpandBom && (
      <div data-testid={`${idPrefix}pos-combo-components-${item.id}`}>
        {bomComponents.map((component, componentIndex) => (
          <ComboComponentRow
            key={`${component.sku}-${componentIndex}`}
            component={component}
            multiplier={item.quantity}
            columns={comboComponentColumns({
              showSerialCol,
              showWeightCol,
              showSellerCol,
              showWarehouseCol,
              isB2B,
              qtyColWidth,
              totalColWidth,
            })}
            indent={26}
            testId={`${idPrefix}pos-item-${item.id}-combo-component-row-${component.sku}`}
          />
        ))}
      </div>
    )}
    </>
  );
}

/** Gom các dòng liên tiếp thành khối: cụm combo hoặc dòng lẻ đơn chiếc. */
type RenderBlock =
  | { kind: 'loose'; item: OrderItem }
  | { kind: 'combo'; groupId: string; members: OrderItem[] };

export function groupItemsForRender(items: OrderItem[]): RenderBlock[] {
  const blocks: RenderBlock[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    if (!item.comboGroupId) {
      blocks.push({ kind: 'loose', item });
      continue;
    }
    if (seen.has(item.comboGroupId)) continue;
    seen.add(item.comboGroupId);
    blocks.push({
      kind: 'combo',
      groupId: item.comboGroupId,
      members: items.filter((candidate) => candidate.comboGroupId === item.comboGroupId),
    });
  }
  return blocks;
}

function ComboGroupRow({
  groupId,
  lineVatById,
  lineTotalById,
  members,
  index,
  idPrefix = '',
  fulfillmentMode,
  showSerialCol,
  showWeightCol,
  isB2B,
  hasDiscountPermission,
  qtyColWidth,
  totalColWidth,
  stockStoreId,
  onUpdateMember,
  itemErrors,
  onSplitCombo,
  onRemoveCombo,
  onChangeComboCount,
  onPickWarehouse,
  onPickGroupWarehouse,
  comboMaxCount,
  comboCountPendingGroupId,
  comboStepperDisabled = false,
  hideComboRemove = false,
  defaultExpanded = true,
}: {
  groupId: string;
  members: OrderItem[];
  index: number;
  /** Tiền tố cho data-testid — dùng để tránh trùng testid giữa bảng desktop và
   *  danh sách mobile (hai khối cùng render trong DOM, chỉ khác nhau ẩn/hiện
   *  bằng CSS media query chứ không unmount, nên testid trùng sẽ vỡ getByTestId). */
  idPrefix?: string;
  fulfillmentMode: FulfillmentMode;
  /** Bật cột Mã QC — cần để căn cột đúng với header (xem `TableHeader`). */
  showSerialCol: boolean;
  /** Bật cột TL (trọng lượng) — cần để căn cột đúng với header (xem `TableHeader`). */
  showWeightCol: boolean;
  /** KH tổ chức (B2B): cột CK%/CK đổi thành "Giá gợi ý" — combo phải khớp. */
  isB2B: boolean;
  /** Bề rộng động — xem `computeDynamicColWidths`, phải khớp `TableHeader`/`OrderItemRow`. */
  qtyColWidth: string;
  totalColWidth: string;
  /**
   * TCR-2475 — store dùng để tra TỒN của kho đã chọn (cột "Tồn", chỉ B2B).
   */
  stockStoreId?: string;
  /**
   * Quyền pos.discount — gate ô CK% của cụm combo (xem `case 'ckPercent'` bên
   * dưới). Cùng hợp đồng với `Props.hasDiscountPermission` ở component cha.
   */
  hasDiscountPermission: boolean;
  /**
   * Update TỪNG dòng thành phần theo id — TÁI DÙNG thẳng `onUpdateItem` gốc
   * của `OrderItemsTable` (cùng chữ ký, cùng function reference), KHÔNG phải
   * hàm mới. Cần prop riêng vì `ComboGroupRow` trước đây chỉ nhận các thao
   * tác áp cho CẢ cụm (`onSplitCombo`/`onRemoveCombo`/`onChangeComboCount`),
   * không có cách nào update một dòng con riêng lẻ.
   */
  onUpdateMember: (itemId: string, patch: Partial<OrderItem>) => void;
  /**
   * TCR-2058 — VAT từng dòng SAU khi phân bổ voucher/KM/điểm (cùng số BE lưu và
   * lên HĐĐT), khoá theo item id. `itemVatAmount` chỉ là VAT TRƯỚC phân bổ nên
   * chỉ làm fallback khi không truyền / thiếu id.
   */
  lineVatById?: ReadonlyMap<string, number>;
  /**
   * TCR-2224 — tiền cả dòng đã gồm VAT, làm tròn về đồng (`line_total`): tổng khách trả
   * = Σ số này. Khoá theo item id. Không truyền / thiếu id ⇒ không hiện (đơn cũ).
   */
  lineTotalById?: ReadonlyMap<string, number>;
  /** Lỗi theo item id — dòng thành phần cần đọc `warehousePick` của CHÍNH nó. */
  itemErrors?: Record<string, PosItemError>;
  onSplitCombo?: (comboGroupId: string) => void;
  onRemoveCombo?: (comboGroupId: string) => void;
  onChangeComboCount?: (comboGroupId: string, nextCount: number) => void;
  /** Mở dialog "Chọn kho xuất hàng" cho ĐÚNG dòng thành phần (theo item id). */
  onPickWarehouse: (itemId: string) => void;
  /** Mở dialog "Chọn kho xuất hàng cho cả cụm" (Việc 4) — theo group id. */
  onPickGroupWarehouse?: (comboGroupId: string) => void;
  comboMaxCount?: (comboGroupId: string) => number;
  comboCountPendingGroupId?: string | null;
  comboStepperDisabled?: boolean;
  hideComboRemove?: boolean;
  /** Mặc định mở/đóng cụm khi lần đầu render — xem comment trên `Props.comboDefaultExpanded`. */
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  /**
   * Các dòng THÀNH VIÊN là combo THẬT đang bung xem thành phần. Khoá theo id
   * dòng: một cụm ảo có thể chứa nhiều combo thật, mỗi cái bung/thu độc lập.
   * Mặc định ĐÓNG — mở sẵn cả hai tầng một lúc thì cụm ảo đổ ra quá dài.
   */
  const [expandedMemberIds, setExpandedMemberIds] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const toggleMemberBom = (memberId: string) => {
    setExpandedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  };
  const head = members[0];
  // `?? 1` chỉ đỡ null/undefined — dữ liệu hỏng gửi 0 (hoặc âm) sẽ làm tổng cụm
  // về 0 và stepper hiển thị "0". Cùng guard với `groupOrderItemsByCombo` ở
  // phía màn đơn hàng (Orders/utils) để hai bảng không lệch nhau.
  const unitCount = head.comboUnitCount && head.comboUnitCount > 0 ? head.comboUnitCount : 1;
  // Giá lẻ tham chiếu (tổng giá bán lẻ gốc của 1 combo) — cố định, dùng làm cơ
  // số phân bổ tỉ lệ CK xuống từng thành phần (KHÔNG còn dùng để suy CK%/CK
  // của header — xem `comboAppliedUnitPrice` bên dưới).
  const retailUnitTotal = head.comboRetailTotal ?? 0;
  // Giá/1 combo LIVE (Σ member.price hiện tại) — CHỈ dùng làm cơ số suy ngược
  // giá lẻ ước tính của TỪNG THÀNH PHẦN trong `comboMemberPricing` khi dòng
  // thiếu snapshot per-line (`comboComponentRetailPrice`), KHÔNG còn dùng để
  // hiện Giá Tiền/CK%/CK của HEADER — xem `comboAppliedUnitPrice`.
  const currentComboUnitPriceLive = members.reduce(
    (sum, m) => sum + m.price * (m.comboUnitQty ?? 1),
    0,
  );
  // Issue (2026-08-08) — Giá Tiền/CK%/CK của header giờ là MỘT CẶP TỰ TRỊ
  // (giá gốc, %CK) giống HỆT `item.price`/`discountPercent` của dòng thường,
  // KHÔNG còn suy ra bằng cách so `currentComboUnitPriceLive` với
  // `retailUnitTotal` mỗi lần render — cách cũ khiến CK% tự nhảy lên 100% khi
  // gõ giá về 0đ (100% = luôn so với mốc giá lẻ cố định), gây hiểu lầm "mất
  // hết combo". `comboAppliedUnitPrice` mặc định về `head.comboUnitPrice`
  // (giá combo lúc thêm giỏ) cho tới khi thu ngân sửa gì đó, sau đó GIỮ
  // NGUYÊN qua các lần sửa tiếp theo — đúng bất biến `handlePriceBlur` dòng
  // thường (sửa Giá Tiền KHÔNG đụng %CK đang có, ngược lại cũng vậy).
  const comboAppliedUnitPrice = head.comboAppliedUnitPrice ?? head.comboUnitPrice ?? 0;
  const comboDiscountPercent = head.comboDiscountPercent ?? 0;
  const comboDiscountAmount = head.comboDiscountAmount ?? 0;
  const comboTotal = comboAppliedUnitPrice * unitCount - comboDiscountAmount;
  const maxCount = comboMaxCount ? comboMaxCount(groupId) : unitCount;
  // Việc 4 — nút "Chọn kho cả cụm" chỉ có nghĩa khi ÍT NHẤT 1 thành phần
  // `canPickWarehouse` (hàng serial lấy kho theo serial, PREORDER không xuất
  // kho ngay — không có gì để chọn ở cấp cụm nếu rỗng).
  const pickableMembers = pickableComboMembers(members, fulfillmentMode);

  // Áp MỘT cặp (giá gốc combo, %CK combo) mới — chốt lại `comboAppliedUnitPrice`/
  // `comboDiscountPercent`/`comboDiscountAmount` trên MỌI thành phần (đều mang
  // bản sao, cùng quy ước với `comboRetailTotal`/`comboUnitCount`), rồi phân bổ
  // giá cuối cùng của MỘT combo (`effectiveUnitPrice`) xuống từng thành phần
  // theo đúng tỉ lệ giá lẻ snapshot — logic phân bổ này KHÔNG đổi so với trước,
  // chỉ đổi nguồn tính `effectiveUnitPrice` (từ cặp giá gốc/%CK tự trị thay vì
  // so trực tiếp với `retailUnitTotal`).
  function applyComboState(nextAppliedPrice: number, nextDiscountPercent: number) {
    const discPerUnit = (nextAppliedPrice * nextDiscountPercent) / 100;
    const nextDiscountAmount = Math.round(discPerUnit * unitCount);
    const effectiveUnitPrice = Math.max(0, Math.round(nextAppliedPrice - discPerUnit));
    // KHÔNG còn tỷ trọng giá lẻ để phân bổ (combo tặng kèm/giá quảng cáo 0đ —
    // MỌI thành phần retail_price = 0, vd "ZEROTEST-ALL") → chia ĐỀU theo
    // `comboUnitQty`, CÙNG quy ước với nhánh fallback của
    // `resolveComboLinePrices` (`@tcr-erp/shared`, hàm BE dùng để chốt số — hai
    // bên phải nói cùng một thứ tiếng; quy ước chia đều khi tổng giá lẻ = 0 nằm
    // ở `priceComboComponents` mà hàm đó rơi về). Trước đây nhánh này bị CHẶN HẲN (`retailUnitTotal <= 0` ở
    // handler return sớm, không gọi vào đây) — gõ giá mới cho đúng loại combo
    // NÀY bị coi như không hợp lệ và ô Giá Tiền lặng lẽ rơi về giá cũ sau khi
    // blur, dù thu ngân KHÔNG thấy lỗi nào.
    const totalQty = members.reduce((sum, m) => sum + (m.comboUnitQty ?? 1), 0);
    const equalUnitPrice = totalQty > 0 ? Math.round(effectiveUnitPrice / totalQty) : 0;
    const pct =
      retailUnitTotal > 0 ? ((retailUnitTotal - effectiveUnitPrice) / retailUnitTotal) * 100 : 0;
    for (const member of members) {
      // Cơ số giá gốc: ƯU TIÊN snapshot cấu hình combo
      // (`comboComponentRetailPrice`) — CÙNG quy tắc `comboMemberPricing` ở
      // trên (đọc kỹ comment tại đó): snapshot mới là cơ số mà
      // `resolveComboLinePrices` dùng để phân bổ `price`/tính `comboRetailTotal`,
      // nên chiết khấu các dòng thành phần cộng lại mới ra đúng chiết khấu của
      // cụm. `retailPrice` (giá lẻ TƯƠI lúc thêm vào giỏ) có thể đã trôi khỏi
      // snapshot bất kỳ lúc nào bảng giá đổi SAU khi combo được cấu hình — dùng
      // nhầm nó làm cơ số từng gây sự cố thật trên dữ liệu thật (dòng hiện CK
      // 99% thay vì 27% đúng của cụm). `member.price` chỉ là chốt cuối khi dòng
      // không có cả hai (không nên xảy ra với dòng combo hợp lệ).
      const originalUnitPrice =
        member.comboComponentRetailPrice ?? member.retailPrice ?? member.price;
      const nextPrice =
        retailUnitTotal > 0
          ? Math.max(0, Math.round(originalUnitPrice * (1 - pct / 100)))
          : equalUnitPrice;
      // `totalAmount` PHẢI được tính lại ở đây — `updateItem` (POS/index.tsx)
      // chỉ shallow-merge patch, không tự suy ra gì cả. `computePosTotals`
      // (nguồn tổng DUY NHẤT cho PaymentFooter + checkout) cộng thẳng
      // `item.totalAmount`; cột "T.Tiền" của dòng thành phần cũng đọc thẳng
      // `member.totalAmount` — bỏ qua bước này thì tổng tiền hiện trên màn
      // thanh toán không đổi dù giá đã giảm.
      //
      // `discountPercent`/`discountAmount` CỦA THÀNH PHẦN CỐ ĐỊNH 0 — CÙNG quy
      // ước với MỌI code path combo khác (`applyCombo.ts`): chiết khấu combo
      // được GỘP THẲNG vào `price` của thành phần (đơn giá đã giảm), không
      // tách riêng thành field discount ở CẤP THÀNH PHẦN. BE cũng luôn force
      // `discount_percent = 0` cho dòng combo bất kể FE gửi gì
      // (`pos-order.service.ts`) — gửi khác 0 không có tác dụng gì ngoài làm
      // hỏng hiển thị FE. Mức CK% "thật" của cả cụm nằm ở `comboDiscountPercent`
      // (cấp HEADER, field mới, không gửi lên BE — xem JSDoc trong `types.ts`).
      const totalAmount = nextPrice * member.quantity;
      onUpdateMember(member.id, {
        price: nextPrice,
        discountPercent: 0,
        discountAmount: 0,
        totalAmount,
        comboAppliedUnitPrice: nextAppliedPrice,
        comboDiscountPercent: nextDiscountPercent,
        comboDiscountAmount: nextDiscountAmount,
      });
    }
  }

  // ---- Giá Tiền (mở khoá — Issue #1: hợp nhất trạng thái edit với dòng thường) ----
  const [editComboPrice, setEditComboPrice] = useState<string | null>(null);
  const comboPriceDisplay =
    editComboPrice !== null ? editComboPrice : formatPrice(comboAppliedUnitPrice);
  function handleComboPriceFocus() {
    setEditComboPrice(formatPrice(comboAppliedUnitPrice));
  }
  function handleComboPriceChange(raw: string) {
    setEditComboPrice(formatPriceInput(raw));
  }
  function handleComboPriceBlur() {
    const parsed = parsePrice(editComboPrice ?? '');
    setEditComboPrice(null);
    // KHÔNG guard theo `retailUnitTotal` ở đây nữa — `applyComboState` tự lo
    // trường hợp combo không có tỷ trọng giá lẻ nào (chia đều). Guard cũ chặn
    // MỌI lần sửa giá của combo 0đ (vd "ZEROTEST-ALL") và khiến ô Giá Tiền
    // lặng lẽ rơi về giá cũ sau khi blur — bug đã gặp thực tế.
    if (parsed === comboAppliedUnitPrice || parsed < 0) return;
    // Giữ NGUYÊN %CK hiện tại — đúng bất biến `handlePriceBlur` của dòng
    // thường (sửa Giá Tiền KHÔNG suy lại %CK từ so sánh với giá lẻ, nên gõ
    // 0đ không còn làm %CK nhảy lên 100%).
    applyComboState(parsed, comboDiscountPercent);
  }

  // ---- CK% ----
  const [editComboCkPct, setEditComboCkPct] = useState<string | null>(null);
  const comboCkPctDisplay =
    editComboCkPct !== null ? editComboCkPct : String(comboDiscountPercent);
  function handleComboCkPctFocus() {
    setEditComboCkPct(String(comboDiscountPercent));
  }
  function handleComboCkPctChange(raw: string) {
    if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
    const num = parseFloat(raw);
    if (raw !== '' && !Number.isNaN(num) && num > 100) return;
    setEditComboCkPct(raw);
  }
  function handleComboCkPctBlur() {
    let parsed = parseDecimal(editComboCkPct ?? '');
    parsed = Math.min(100, Math.max(0, parsed));
    setEditComboCkPct(null);
    if (parsed === comboDiscountPercent) return;
    applyComboState(comboAppliedUnitPrice, parsed);
  }

  // ---- CK amount ----
  const [editComboCkAmt, setEditComboCkAmt] = useState<string | null>(null);
  const comboCkAmtDisplay =
    editComboCkAmt !== null ? editComboCkAmt : formatPrice(comboDiscountAmount);
  function handleComboCkAmtFocus() {
    setEditComboCkAmt(formatPrice(comboDiscountAmount));
  }
  function handleComboCkAmtChange(raw: string) {
    setEditComboCkAmt(formatPriceInput(raw));
  }
  function handleComboCkAmtBlur() {
    const parsedAmount = parsePrice(editComboCkAmt ?? '');
    setEditComboCkAmt(null);
    const maxDiscount = comboAppliedUnitPrice * unitCount;
    const clamped = Math.min(Math.max(parsedAmount, 0), maxDiscount);
    if (clamped === comboDiscountAmount) return;
    const perUnitAmount = unitCount > 0 ? clamped / unitCount : 0;
    const nextPercent =
      comboAppliedUnitPrice > 0
        ? Math.round((perUnitAmount / comboAppliedUnitPrice) * 100 * 100) / 100
        : 0;
    applyComboState(comboAppliedUnitPrice, nextPercent);
  }

  // Việc 3 (UI fix 3) — ô SL của cụm giờ là Input có khung, GIỐNG hệt ô SL của
  // dòng thường (`OrderItemRow`, xem `handleQtyFocus/Change/Blur` bên trên),
  // thay vì stepper "−/+". Cùng pattern editing-state: `null` = không focus
  // (hiện `unitCount` committed), string = user đang gõ (hiện raw input).
  //
  // BẤT BIẾN CỨNG: số hiện/sửa ở đây là SỐ COMBO (`comboUnitCount`), KHÔNG
  // phải `quantity` của bất kỳ dòng nào — và đổi số CHỈ đi qua
  // `onChangeComboCount(groupId, nextCount)`, không bao giờ set thẳng
  // `quantity`. Lý do: BE giữ bất biến `quantity == comboUnitQty ×
  // comboUnitCount`; set thẳng `quantity` phá bất biến đó — dòng nhóm và
  // footer hiện hai tổng khác nhau, BE trả 400 lúc thanh toán.
  const [editComboCount, setEditComboCount] = useState<string | null>(null);
  const comboCountDisplay = editComboCount !== null ? editComboCount : String(unitCount);

  function handleComboCountFocus() {
    setEditComboCount(String(unitCount));
  }
  function handleComboCountChange(raw: string) {
    // Cùng guard với `handleQtyChange` của dòng thường — chỉ digit (hoặc rỗng
    // trong lúc gõ), không cho gõ dấu trừ/thập phân.
    if (raw !== '' && !/^\d*$/.test(raw)) return;
    setEditComboCount(raw);
  }
  function handleComboCountBlur() {
    const parsed = parseInt(editComboCount ?? '', 10);
    setEditComboCount(null);
    // Màn sửa đơn (`comboStepperDisabled`) — Input tự chặn tương tác thật của
    // người dùng qua `disabled`, nhưng test (và một số trình duyệt) vẫn có
    // thể bắn sự kiện change/blur theo chương trình vào input đã disabled —
    // guard tường minh ở đây để KHÔNG BAO GIỜ gọi `onChangeComboCount` khi bị
    // khoá, khớp đúng hành vi 2 nút +/- disabled trước đây.
    if (comboStepperDisabled) return;
    // Rỗng/không phải số → coi như 1 (không phải 0 — đỡ mất cả cụm).
    //
    // TCR-982: KHÔNG kẹp trần theo `maxCount` nữa. `maxCount` chỉ đếm hàng đang
    // có trong giỏ, nên gõ 12 khi giỏ có 1 combo bị trả về 1 dù kho còn thừa
    // hàng. Trần thật (theo TỒN) chỉ biết được sau khi tra tồn tươi — việc đó
    // do `onChangeComboCount` làm, và nó tự kẹp + báo lý do.
    const clamped = Number.isNaN(parsed) ? 1 : Math.max(1, parsed);
    if (clamped === unitCount) return;
    onChangeComboCount?.(groupId, clamped);
  }

  const td: React.CSSProperties = {
    padding: '8px 4px',
    fontSize: 13,
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  const groupColumns = comboGroupColumns(showSerialCol, showWeightCol, isB2B, qtyColWidth, totalColWidth);

  /** Ô của hàng NỘI DUNG (Input/Button thật) — 1 cột trong `groupColumns`. */
  function renderContentCell(col: ComboGroupColumn): React.ReactNode {
    const style: React.CSSProperties = col.flex
      ? { ...td, flex: col.flex, minWidth: col.minWidth }
      : { ...td, width: col.width };
    switch (col.key) {
      case 'index':
        return (
          <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
            {index}
          </div>
        );
      case 'name':
        return (
          <div key={col.key} style={{ ...style, gap: 6 }}>
            <Button
              type="text"
              size="small"
              data-testid={`pos-combo-toggle-${idPrefix}${groupId}`}
              onClick={() => setExpanded((prev) => !prev)}
              style={{ padding: 0, height: 'auto', color: '#a4193d' }}
            >
              {expanded ? '▾' : '▸'}
            </Button>
            <Tag color="#a4193d" style={{ margin: 0, fontSize: 9, fontWeight: 700 }}>COMBO</Tag>
            {/* Việc 1 (UI fix 3) — khối dọc chứa tên/mã combo VÀ nút "Chọn kho"
                (Việc 4) NGAY DƯỚI, không còn ngang hàng với nút "Tách". KHÔNG
                set `alignItems` ở container này — mặc định 'stretch' của
                flex-column vẫn đúng cho 2 dòng Text (tên/mã combo có thể dài,
                cần bọc theo bề rộng cột thay vì tràn). Fix căn trái nút "Chọn
                kho" đặt ở `alignSelf` của CHÍNH nút đó (xem bên dưới), không
                đụng hành vi của 2 dòng Text. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Text strong style={{ fontSize: 13 }}>{head.comboName}</Text>
              <Text type="secondary" style={{ fontSize: 10 }}>
                {head.comboSku} · {members.length} SP
              </Text>
              {/* Việc 4 — chọn 1 kho ở đây, áp cho MỌI thành phần bên dưới cùng
                  lúc (`ComboWarehousePickerDialog`). Nút chọn kho riêng từng
                  thành phần (Vấn đề 3) vẫn giữ nguyên bên dưới — thu ngân đổi
                  riêng được sau khi áp hàng loạt, vì các SP trong combo có thể
                  nằm ở kho khác nhau.
                  Việc 1 — BẮT BUỘC `alignSelf: 'flex-start'`: mặc định 'stretch'
                  của cột cha kéo Button giãn theo item rộng nhất (tên combo, dài
                  ngắn tuỳ combo); antd Button tự `justify-content: center` nội
                  dung CHÍNH nó nên icon+chữ tự canh giữa trong khung đã giãn đó
                  ⇒ trông như lệch phải, lệch nhiều/ít tuỳ độ dài tên combo.
                  `alignSelf: 'flex-start'` co RIÊNG nút này về đúng bề rộng nội
                  dung, neo mép trái thẳng với tên/mã combo. */}
              {pickableMembers.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  danger={pickableMembers.some((m) => itemErrors?.[m.id]?.warehousePick)}
                  data-testid={`pos-combo-pick-warehouse-${idPrefix}${groupId}`}
                  style={{ padding: 0, height: 'auto', fontSize: 11, alignSelf: 'flex-start' }}
                  onClick={() => onPickGroupWarehouse?.(groupId)}
                >
                  📦 {comboWarehouseButtonLabel(pickableMembers)}
                </Button>
              )}
            </div>
            <Button size="small" data-testid={`pos-combo-split-${idPrefix}${groupId}`} onClick={() => onSplitCombo?.(groupId)}>
              Tách
            </Button>
          </div>
        );
      case 'serial':
        // Mã QC — combo không có QC ở cấp cụm, chỉ giữ chỗ để căn cột với header.
        return (
          <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
            <span style={{ color: '#bbb' }}>—</span>
          </div>
        );
      case 'price':
        // Giá Tiền — issue #1 (2026-08-08): mở khoá y hệt dòng thường. Sửa ô
        // này đổi giá của CẢ CỤM, phân bổ đều xuống mọi thành phần qua
        // `applyComboDiscountPercent` (xem comment tại đó) — không còn "chỉ
        // đọc, sửa qua CK%" như thiết kế cũ.
        return (
          <div key={col.key} style={style}>
            <Input
              data-testid={`pos-combo-price-${idPrefix}${groupId}`}
              style={{ width: '100%', textAlign: 'right' }}
              value={comboPriceDisplay}
              onFocus={handleComboPriceFocus}
              onChange={(e) => handleComboPriceChange(e.target.value)}
              onBlur={handleComboPriceBlur}
              onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
              inputMode="numeric"
              disabled={!hasDiscountPermission}
            />
          </div>
        );
      case 'stock':
        // TCR-2475 — cụm combo KHÔNG có tồn riêng (combo ảo gồm nhiều dòng
        // giỏ, mỗi dòng một kho khác nhau) ⇒ chỉ giữ ô trống để căn cột,
        // tồn thật nằm ở dòng thành phần bên dưới.
        return (
          <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
            <Tooltip title="Combo không có tồn riêng — xem tồn ở từng thành phần bên dưới">
              <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
            </Tooltip>
          </div>
        );
      case 'suggest':
        return (
          <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
            <Tooltip title="Combo không có Giá gợi ý riêng theo CKTM">
              <Text type="secondary" style={{ fontSize: 12 }}>—</Text>
            </Tooltip>
          </div>
        );
      case 'discountType':
        // TCR-1154 — Loại CK (theo % hay theo ₫) của cụm combo. Chỉ là tuỳ chọn
        // HIỂN THỊ/THAO TÁC — không đổi cách lưu (comboDiscountPercent/Amount giữ nguyên).
        return (
          <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
            <Select
              size="small"
              data-testid={`pos-combo-discount-type-${idPrefix}${groupId}`}
              value={head.comboDiscountType ?? 'percent'}
              onChange={(type: 'percent' | 'amount') =>
                members.forEach((m) => onUpdateMember(m.id, { comboDiscountType: type }))
              }
              options={[
                { value: 'percent', label: '%' },
                { value: 'amount', label: '₫' },
              ]}
              disabled={!hasDiscountPermission}
              style={{ width: '100%' }}
              aria-label="Loại chiết khấu"
            />
          </div>
        );
      case 'discount':
        if ((head.comboDiscountType ?? 'percent') === 'percent') {
          return (
            <div key={col.key} className={col.className} style={{ ...style, justifyContent: 'center' }}>
              <Input
                data-testid={`pos-combo-ckpct-${idPrefix}${groupId}`}
                style={{ width: '100%', textAlign: 'center' }}
                value={comboCkPctDisplay}
                onFocus={handleComboCkPctFocus}
                onChange={(e) => handleComboCkPctChange(e.target.value)}
                onBlur={handleComboCkPctBlur}
                onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                inputMode="decimal"
                suffix="%"
                disabled={!hasDiscountPermission}
              />
            </div>
          );
        }
        return (
          <div key={col.key} style={style}>
            <Input
              data-testid={`pos-combo-ckamt-${idPrefix}${groupId}`}
              style={{ width: '100%', textAlign: 'right' }}
              value={comboCkAmtDisplay}
              onFocus={handleComboCkAmtFocus}
              onChange={(e) => handleComboCkAmtChange(e.target.value)}
              onBlur={handleComboCkAmtBlur}
              onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
              inputMode="numeric"
              suffix="₫"
              disabled={!hasDiscountPermission}
            />
          </div>
        );
      case 'vat':
        // VAT — không có giá trị gộp hợp lý ở cấp cụm (mỗi thành phần có thể
        // khác VAT sản phẩm) — giữ khung Input disabled cho khớp cột, hiện "—".
        return (
          <div key={col.key} style={style}>
            <Input
              data-testid={`pos-combo-vat-${idPrefix}${groupId}`}
              aria-label="VAT (combo — không sửa)"
              style={{ width: '100%', textAlign: 'center' }}
              value="—"
              disabled
            />
          </div>
        );
      case 'qty':
        // Việc 3 (UI fix 3) — cùng khung Input với ô SL của dòng thường
        // (`OrderItemRow`, `isSlEditable` branch), thay vì stepper "−/+". Số
        // hiện/sửa là SỐ COMBO (`comboUnitCount`) — đổi CHỈ qua
        // `onChangeComboCount`, kẹp [1, maxCount] ở `handleComboCountBlur`
        // (không có state riêng ở đây thay cho nút "+" disable khi chạm trần
        // trước đây). Giữ nguyên 2 tooltip gốc của stepper — thu ngân vẫn cần
        // biết vì sao không đổi được.
        return (
          <div key={col.key} style={style}>
            <Tooltip
              title={
                comboStepperDisabled
                  ? 'Không đổi được số lượng combo trên màn sửa đơn — dùng nút "Tách" để bỏ cả cụm'
                  : unitCount >= maxCount
                    ? 'Tăng thêm sẽ tự thêm hàng vào giỏ nếu kho còn đủ tồn'
                    : undefined
              }
            >
              <Input
                data-testid={`pos-combo-count-${idPrefix}${groupId}`}
                style={{ width: '100%', textAlign: 'center', padding: '4px 2px' }}
                value={comboCountDisplay}
                disabled={comboStepperDisabled || comboCountPendingGroupId === groupId}
                onFocus={handleComboCountFocus}
                onChange={(e) => handleComboCountChange(e.target.value)}
                onBlur={handleComboCountBlur}
                onPressEnter={(e) => (e.target as HTMLInputElement).blur()}
                inputMode="numeric"
              />
            </Tooltip>
          </div>
        );
      case 'weight':
        return <div key={col.key} className={col.className} style={style} />;
      case 'total':
        return (
          <div key={col.key} style={{ ...style, padding: '8px 1px', justifyContent: 'flex-end' }}>
            <Text strong style={{ color: '#a4193d', whiteSpace: 'nowrap' }}>{formatPrice(comboTotal)}</Text>
          </div>
        );
      case 'delete':
        return (
          <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
            {/* ẨN (không disable) — xem comment `hideComboRemove` trên Props:
                xoá cụm không persist qua save trên màn sửa đơn, và không có
                hành động thay thế nào để tooltip trỏ tới. */}
            {!hideComboRemove && (
              <Button
                type="text"
                danger
                size="small"
                data-testid={`pos-combo-remove-${idPrefix}${groupId}`}
                icon={<DeleteOutlined />}
                onClick={() => onRemoveCombo?.(groupId)}
              />
            )}
          </div>
        );
      default:
        return <div key={col.key} style={style} />;
    }
  }

  return (
    <>
      <div
        data-testid={`pos-combo-row-${idPrefix}${groupId}`}
        style={{ display: 'flex', flexDirection: 'column', background: '#fffafb', borderBottom: '1px solid #f5e6ea' }}
      >
        {/* MỘT hàng ô duy nhất. Issue (2026-08-08): Giá Tiền/CK%/CK giờ là một
            cặp (giá gốc, %CK) tự trị — `comboAppliedUnitPrice`/
            `comboDiscountPercent`/`comboDiscountAmount` — giống hệt
            `item.price`/`discountPercent`/`discountAmount` của dòng thường,
            không còn hàng phụ "giá lẻ gạch ngang" riêng, 3 ô này tự đọc thành
            một phép tính đầy đủ giống dòng thường. */}
        <div style={{ display: 'flex' }}>{groupColumns.map((col) => renderContentCell(col))}</div>
      </div>

      {expanded &&
        members.map((member) => {
          const memberError = itemErrors?.[member.id];
          const memberPricing = comboMemberPricing(member, { retailUnitTotal, comboUnitPrice: currentComboUnitPriceLive });
          /**
           * TCR-1517 — COMBO THẬT NẰM TRONG CỤM COMBO ẢO. BE cho phép một biến
           * thể combo thật làm thành phần của combo ảo (khoá bằng
           * `tcr-erp-be-tenant/src/modules/combo/__tests__/combo-suggest-with-real-combo.spec.ts`),
           * nên một dòng giỏ mang ĐỒNG THỜI `comboGroupId` và `bom_components`.
           * Dòng đó rơi vào đây — trong cụm — chứ không còn đi qua
           * `OrderItemRow`, nên nếu không lồng bảng thành phần vào đây thì nút
           * "Xem thành phần" BIẾN MẤT ngay giữa ca bán (thu ngân thêm combo
           * thật vào giỏ, bấm banner gợi ý gộp combo ảo là mất). Màn sửa đơn
           * render chính bảng này nên dính y hệt.
           */
          const memberBom = member.bom_components ?? [];

          /** Ô của dòng THÀNH PHẦN — CÙNG `groupColumns` với 2 hàng phía trên
           *  (bản chép tay thứ BA trước đây, cùng nguy cơ lệch cột như hàng
           *  phụ — gộp về 1 nguồn cho cả 3 hàng, xem `comboGroupColumns`). */
          function renderMemberCell(col: ComboGroupColumn): React.ReactNode {
            const style: React.CSSProperties = col.flex
              ? { ...td, flex: col.flex, minWidth: col.minWidth }
              : { ...td, width: col.width };
            switch (col.key) {
              case 'name':
                return (
                  <div key={col.key} style={{ ...style, paddingLeft: 26, alignItems: 'flex-start', gap: 8 }}>
                    {/* TCR-1517 — thành viên cụm ảo LÀ combo thật: mũi tên bung
                        thành phần của CHÍNH nó, đứng trước ảnh y như dòng lẻ. */}
                    {memberBom.length > 0 && (
                      <div style={{ flex: 'none', paddingTop: 10 }}>
                        <ComboExpandToggle
                          expanded={expandedMemberIds.has(member.id)}
                          onToggle={() => toggleMemberBom(member.id)}
                          testId={`${idPrefix}pos-combo-member-components-toggle-${member.id}`}
                        />
                      </div>
                    )}
                    {/* Việc 3 — ảnh sản phẩm, cao ~60px (≈3 dòng: tên/SKU/nút chọn
                        kho). Dòng dựng từ đơn ĐÃ LƯU không có `productImage` (chỉ
                        dòng dựng từ tra cứu mới có, xem `OrderItem.productImage`) —
                        `src=''` + `fallback` (cùng khuôn `QRSuggestItem`) hiện
                        placeholder xám thay vì icon ảnh vỡ, không vỡ layout. */}
                    <Image
                      src={member.productImage || ''}
                      alt={member.variantName}
                      width={48}
                      height={60}
                      preview={false}
                      style={{ objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
                      fallback={POS_IMAGE_FALLBACK}
                    />
                    {/* KHÔNG set `alignItems` ở đây — mặc định 'stretch' của
                        flex-column là ĐÚNG cho 2 dòng Text (tên/SKU): nó cho phép
                        `wordBreak: 'break-word'` bọc chữ trong đúng bề rộng cột
                        khi tên SP dài. Nếu đổi thành 'flex-start' để "sửa" nút
                        Chọn kho, Text sẽ co về bề rộng nội dung của CHÍNH nó
                        (shrink-to-fit) thay vì bọc theo cột — tên SP dài sẽ TRÀN
                        ra ngoài thay vì xuống dòng. Xem `alignSelf` trên Button
                        bên dưới cho fix ĐÚNG chỗ (chỉ cho item đó, không đụng 2
                        dòng Text). */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                      {/* TCR-1517 — thành viên cụm ảo LÀ combo thật: nhãn giống
                          hệt dòng lẻ (`OrderItemRow`) và màn chi tiết đơn, để
                          thu ngân đọc được bảng thành phần lồng bên dưới là của
                          dòng nào. `alignSelf: 'flex-start'` vì cột cha đang
                          stretch (xem ghi chú dài ở nút Chọn kho bên dưới). */}
                      {member.is_combo_product && (
                        <Tag
                          color="#a4193d"
                          data-testid={`${idPrefix}pos-combo-product-tag-${member.id}`}
                          style={{ margin: 0, alignSelf: 'flex-start', fontSize: 9, lineHeight: '14px', padding: '0 4px' }}
                        >
                          Combo
                        </Tag>
                      )}
                      {/* Cùng guard với bảng phía Orders (`member.combo_unit_qty ?? 1`)
                          — render trần thì dữ liệu hỏng hiện ra "undefined×". */}
                      <Text style={{ fontSize: 12, color: '#333', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {member.comboUnitQty ?? 1}× {member.variantName}
                      </Text>
                      {/* Việc 2 — mã SKU xuống dòng riêng dưới tên (kiểu chữ phụ), bám
                          đúng cách dòng hàng thường hiện mã biến thể (xem
                          `OrderItemRow` — `td` "Tên Hàng"), thay vì nối đuôi tên. */}
                      <Text type="secondary" style={{ fontSize: 10, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {member.variantCode}
                      </Text>
                      {/* Vấn đề 3 (BLOCKER): mỗi thành phần combo có kho xuất RIÊNG — các
                          SP trong combo có thể nằm ở kho khác nhau. Đường tìm kiếm dựng
                          dòng qua `variantToOrderItem` (không set `warehouseId`), khác
                          đường "gợi ý gộp" (spread dòng có sẵn trong giỏ nên còn kho từ
                          trước) — nên nút này PHẢI có ở đây, không chỉ ở dòng thường,
                          nếu không thu ngân bị chặn thanh toán không lối thoát. Việc 4
                          thêm nút chọn hàng loạt ở dòng đầu cụm — nút RIÊNG này vẫn giữ
                          để đổi tay sau khi áp hàng loạt.
                          Việc 2 (UI fix 3) — BẮT BUỘC `alignSelf: 'flex-start'`: mặc định
                          'stretch' của cột cha kéo Button giãn theo item rộng nhất (tên SP,
                          dài ngắn khác nhau mỗi dòng thành phần); antd Button tự
                          `justify-content: center` nội dung CHÍNH nó nên icon+chữ tự canh
                          giữa trong khung đã giãn đó — ba dòng thành phần nhìn LỆCH NHAU,
                          lệch nhiều/ít đúng theo độ dài tên SP phía trước (không phải do
                          padding/margin, dù style trông giống hệt nhau). `alignSelf:
                          'flex-start'` co RIÊNG nút này về đúng bề rộng nội dung, neo mép
                          trái, thẳng hàng ở mọi dòng — không đụng hành vi stretch/wrap của
                          2 dòng Text phía trên. */}
                      {canPickWarehouse(member, fulfillmentMode) && (
                        <>
                          <Button
                            type="link"
                            size="small"
                            danger={memberError?.warehousePick}
                            style={{ padding: 0, height: 'auto', fontSize: 11, alignSelf: 'flex-start' }}
                            onClick={() => onPickWarehouse(member.id)}
                          >
                            📦 {member.warehouseName ?? 'Chọn kho'}
                          </Button>
                          {memberError?.warehousePick && (
                            <Text style={{ fontSize: 11, color: '#ff4d4f' }}>Bắt buộc chọn kho xuất hàng</Text>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              case 'price':
                // GIÁ GỐC của thành phần (giá lẻ tươi lúc thêm vào giỏ), không
                // phải đơn giá đã phân bổ — cùng quy ước với dòng cụm phía trên
                // và dòng hàng thường: Giá Tiền là giá TRƯỚC chiết khấu, phần
                // trừ nằm ở cột CK, số cuối nằm ở cột T.Tiền.
                return (
                  <div key={col.key} style={{ ...style, justifyContent: 'flex-end' }}>
                    {formatPrice(memberPricing.unitRetail)}
                  </div>
                );
              case 'discountType':
                // Loại CK chỉ chọn ở dòng đầu cụm — dòng thành phần giữ ô rỗng để căn cột.
                return <div key={col.key} style={style} />;
              case 'discount':
                // Chỉ mang giá trị THAM KHẢO (ước tính phân bổ giá lẻ so với
                // giá đã giảm của riêng dòng này) — không tính được (thiếu cả
                // snapshot lẫn cơ số suy ngược) hoặc ra số không hợp lý
                // (≤ 0, coi như không có CK) thì hiện "-", không bịa số 0.
                if ((head.comboDiscountType ?? 'percent') === 'amount') {
                  return (
                    <div key={col.key} style={{ ...style, justifyContent: 'flex-end' }}>
                      {memberPricing.discountAmount > 0 ? formatPrice(memberPricing.discountAmount) : '-'}
                    </div>
                  );
                }
                return (
                  <div key={col.key} className={col.className} style={{ ...style, justifyContent: 'center' }}>
                    {memberPricing.discountPercent > 0 ? String(memberPricing.discountPercent) : '-'}
                  </div>
                );
              case 'vat':
                // TCR-1154 — VAT theo TỪNG sản phẩm thành phần (kế thừa từ product.vat),
                // hiển thị % + số tiền thuế. Giá trị đã được computePosTotals cộng vào tổng.
                return (
                  <div key={col.key} style={{ ...style, flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                    <Text style={{ fontSize: 12 }}>{member.vat ? `${member.vat}%` : '—'}</Text>
                    <Text type="secondary" style={{ fontSize: 10, lineHeight: 1 }}>
                      {member.vat ? `${formatPrice(lineVatById?.get(member.id) ?? itemVatAmount(member))} đ` : ''}
                    </Text>
                  </div>
                );
              case 'qty':
                return (
                  <div key={col.key} style={{ ...style, justifyContent: 'center' }}>
                    {member.quantity}
                  </div>
                );
              case 'stock':
                // TCR-2475 — tồn của KHO ĐÃ CHỌN của chính thành phần này
                // (mỗi thành phần combo có kho riêng — xem nút "Chọn kho" ở
                // dòng thành phần), cùng quy tắc với dòng hàng thường.
                return (
                  <div
                    key={col.key}
                    style={{ ...style, flexDirection: 'column', justifyContent: 'center', gap: 2 }}
                  >
                    <StockCell
                      variantId={member.variantId}
                      warehouseId={member.warehouseId}
                      quantity={member.quantity}
                      storeId={stockStoreId}
                      testId={`${idPrefix}pos-item-stock-${member.id}`}
                    />
                    <Text type="secondary" style={{ fontSize: 10, lineHeight: 1 }}>
                      {' '}
                    </Text>
                  </div>
                );
              case 'total':
                return (
                  <div
                    key={col.key}
                    style={{
                      ...style,
                      padding: '8px 1px',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      justifyContent: 'center',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatPrice(member.totalAmount)}
                    {lineTotalById?.has(member.id) && (
                      <Tooltip title={LINE_TOTAL_CAPTION_TITLE}>
                        <Text type="secondary" style={{ fontSize: 10, lineHeight: 1 }}>
                          {lineTotalCaption(lineTotalById.get(member.id) ?? 0)}
                        </Text>
                      </Tooltip>
                    )}
                  </div>
                );
              default:
                return <div key={col.key} className={col.className} style={style} />;
            }
          }

          return (
            <Fragment key={member.id}>
              <div
                data-testid={`pos-combo-member-${idPrefix}${member.id}`}
                style={{ display: 'flex', background: '#fafafa', borderBottom: '1px solid #f5f5f5', fontSize: 12, color: '#666' }}
              >
                {groupColumns.map((col) => renderMemberCell(col))}
              </div>
              {/* Bảng thành phần của thành viên LÀ combo thật — CHỈ XEM, nằm
                  ngoài dòng flex ngang để chiếm trọn bề ngang bên dưới dòng đó.
                  KHÔNG có nút bung riêng: cùng cách hiển thị với ca lồng nhau ở
                  màn chi tiết đơn (`ReadOnlyOrderItemsTable` —
                  `read-only-combo-member-bom-row-*`), vì nó đã nằm trong phần
                  bung của cụm ảo rồi, thêm một tầng gấp/mở nữa chỉ giấu dữ liệu
                  sau hai cú bấm. Số lượng nhân theo SL của CHÍNH dòng thành
                  viên (`member.quantity` — số combo thật trong cụm), giống dòng
                  lẻ nhân theo `item.quantity`. */}
              {expandedMemberIds.has(member.id) && (
                <div data-testid={`${idPrefix}pos-combo-member-components-${member.id}`}>
                  {/* Dùng `groupColumns` (lưới cột của CỤM) chứ không phải lưới
                      đầy đủ: dòng thành phần phải thẳng hàng với chính dòng
                      thành viên ngay phía trên nó. */}
                  {memberBom.map((component, componentIndex) => (
                    <ComboComponentRow
                      key={`${component.sku}-${componentIndex}`}
                      component={component}
                      multiplier={member.quantity}
                      columns={groupColumns}
                      indent={52}
                      testId={`${idPrefix}pos-combo-member-${member.id}-combo-component-row-${component.sku}`}
                    />
                  ))}
                </div>
              )}
            </Fragment>
          );
        })}
    </>
  );
}

function MobileOrderItemCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="pos-order-mobile-card">
      <div className="pos-order-mobile-card-summary" aria-label="Tóm tắt dòng hàng mobile">
        <span>Giá</span>
        <span>SL</span>
        <span>Tổng tiền</span>
      </div>
      {children}
    </div>
  );
}

export function OrderItemsTable({
  items, fulfillmentMode, storeId, onUpdateItem, onRemoveItem, onSplitItem, onDropFlash, maxQuantityFor,
  onRebalanceFlashGroup,
  onSwapSerial, swapping, staleItemIds, stalePreviewByItemId, itemErrors,
  showSerialCol, showWeightCol, warehouseStoreId, isB2B = false, discountPolicyEnabled, currentUserName, hasDiscountPermission,
  onSplitCombo, onRemoveCombo, onChangeComboCount, comboMaxCount, comboStepperDisabled,
  comboCountPendingGroupId, hideComboRemove, comboDefaultExpanded = true,
  lockFlashSaleLines, lineVatById, lineTotalById,
}: Props) {
  // [BỎ TRƯỜNG CHỦ KHO] Cột C.Kho (warehouse_staff_id) đã được gỡ khỏi POS giao ngay
  // và trang sửa đơn. Giữ lại toàn bộ code phía dưới để có thể bật lại bằng cách
  // khôi phục dòng gốc bên dưới.
  // const showWarehouseCol = fulfillmentMode !== 'PREORDER';
  const showWarehouseCol = false;
  // [BỎ N.BÁN + NV CHĂM SÓC] Cột N.Bán (seller_staff_id) đã gỡ khỏi POS. Giữ toàn
  // bộ code phía dưới để bật lại bằng cách khôi phục dòng gốc bên dưới.
  // const showSellerCol = true;
  const showSellerCol = false;
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  /**
   * TCR-2475 — store dùng để tra TỒN kho cho cột "Tồn" (chỉ B2B). Ưu tiên
   * store của ĐƠN (`warehouseStoreId` — màn sửa đơn: tồn phải là tồn của
   * chi nhánh của đơn, không phải của ca đang mở), không thì store màn đang
   * chạy (`storeId` — POS: chi nhánh của ca).
   */
  const stockStoreId = warehouseStoreId ?? storeId;
  /**
   * TCR-2475 — sau khi đổi kho thì tra lại tồn (số tồn của kho mới đã có
   * trong cache, nhưng cache có thể cũ). `queryKey` xem `StockCell`.
   */
  const queryClient = useQueryClient();
  // Derive live item từ items mỗi render → dialog luôn phản ánh data hiện tại và
  // tự đóng nếu dòng bị xoá khi dialog đang mở.
  const editingItem = items.find((i) => i.id === editingItemId) ?? null;
  // TCR-629: dòng đang mở modal "Chọn kho xuất hàng" — cùng pattern derive-live.
  const [pickingItemId, setPickingItemId] = useState<string | null>(null);
  const pickingItem = items.find((i) => i.id === pickingItemId) ?? null;
  // Việc 4 — cụm combo đang mở modal "Chọn kho xuất hàng cho cả cụm". Derive
  // live từ `items` mỗi render (cùng pattern trên) để danh sách thành phần +
  // SL cần luôn khớp giỏ hiện tại, kể cả khi giỏ đổi trong lúc dialog mở.
  const [pickingComboGroupId, setPickingComboGroupId] = useState<string | null>(null);
  const pickingComboAllMembers = pickingComboGroupId
    ? items.filter((i) => i.comboGroupId === pickingComboGroupId)
    : [];
  const pickingComboMembers = pickingComboAllMembers.filter((i) => canPickWarehouse(i, fulfillmentMode));
  const pickingComboHead = pickingComboAllMembers[0] ?? null;
  // TCR-2292: "Chọn kho cho tất cả" — mọi dòng chọn được kho của giỏ (hàng lẻ lẫn
  // thành phần combo; hàng serial/PREORDER đã bị `canPickWarehouse` loại).
  const [pickingAll, setPickingAll] = useState(false);
  const pickableAllItems = items.filter((i) => canPickWarehouse(i, fulfillmentMode));
  // Xem `computeDynamicColWidths` — tính MỘT LẦN theo `items` hiện tại, dùng
  // chung cho header + mọi dòng để cột luôn thẳng hàng. PHẢI gọi trước early
  // return "giỏ rỗng" bên dưới — hook không được gọi có điều kiện.
  const { qtyColWidth, totalColWidth } = useMemo(
    () => computeDynamicColWidths(items, lineTotalById),
    [items, lineTotalById],
  );

  const handleConfirmSwap = async (serial: string) => {
    if (!editingItem) return;
    const res = await onSwapSerial(editingItem.id, serial);
    if (res.ok) {
      void message.success('Đã đổi mã quy cách');
      setEditingItemId(null);
    } else {
      void message.error(res.error ?? 'Không thể đổi mã quy cách');
    }
  };

  if (items.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: 13 }}>
        Quét QR hoặc nhập mã để thêm sản phẩm
      </div>
    );
  }

  // Cụm combo hiển thị 1 dòng thu gọn (Task 10) — áp dụng cho cả bảng desktop
  // và danh sách mobile. Hai khối cùng render trong DOM luôn (ẩn/hiện lẫn nhau
  // qua CSS media query, không unmount), nên `ComboGroupRow` nhận `idPrefix`
  // riêng theo variant để data-testid không trùng giữa hai bản — nếu trùng,
  // getByTestId ném lỗi "multiple elements found" dù mắt thường không thấy
  // bản còn lại (display:none không được getByTestId lọc bỏ, khác getByRole).
  const blocks = groupItemsForRender(items);

  const renderBlock = (
    block: RenderBlock,
    blockIndex: number,
    variant: 'desktop' | 'mobile' = 'desktop',
  ) => {
    if (block.kind === 'combo') {
      return (
        <ComboGroupRow
          key={block.groupId}
          groupId={block.groupId}
          lineVatById={lineVatById}
          lineTotalById={lineTotalById}
          members={block.members}
          index={blockIndex + 1}
          idPrefix={variant === 'mobile' ? 'mobile-' : ''}
          fulfillmentMode={fulfillmentMode}
          showSerialCol={showSerialCol}
          showWeightCol={showWeightCol}
          isB2B={isB2B}
          hasDiscountPermission={hasDiscountPermission}
          qtyColWidth={qtyColWidth}
          totalColWidth={totalColWidth}
          stockStoreId={stockStoreId}
          onUpdateMember={onUpdateItem}
          itemErrors={itemErrors}
          onSplitCombo={onSplitCombo}
          onRemoveCombo={onRemoveCombo}
          onChangeComboCount={onChangeComboCount}
          onPickWarehouse={setPickingItemId}
          onPickGroupWarehouse={setPickingComboGroupId}
          comboMaxCount={comboMaxCount}
          comboCountPendingGroupId={comboCountPendingGroupId}
          comboStepperDisabled={comboStepperDisabled}
          hideComboRemove={hideComboRemove}
          defaultExpanded={comboDefaultExpanded}
        />
      );
    }

    const itemRow = (
      <OrderItemRow
        item={block.item}
        lineVatById={lineVatById}
        lineTotalById={lineTotalById}
        index={blockIndex}
        idPrefix={variant === 'mobile' ? 'mobile-' : ''}
        fulfillmentMode={fulfillmentMode}
        showWarehouseCol={showWarehouseCol}
        showSellerCol={showSellerCol}
        showSerialCol={showSerialCol}
        showWeightCol={showWeightCol}
        storeId={storeId}
        stockStoreId={stockStoreId}
        isStale={staleItemIds?.has(block.item.id) ?? false}
        previewPrice={stalePreviewByItemId?.get(block.item.id)?.price}
        error={itemErrors?.[block.item.id]}
        isB2B={isB2B}
        discountPolicyEnabled={discountPolicyEnabled}
        currentUserName={currentUserName}
        hasDiscountPermission={hasDiscountPermission}
        lockFlashSaleLines={lockFlashSaleLines}
        qtyColWidth={qtyColWidth}
        totalColWidth={totalColWidth}
        onUpdate={(patch) => onUpdateItem(block.item.id, patch)}
        onRemove={() => onRemoveItem(block.item.id)}
        onSplit={onSplitItem ? () => onSplitItem(block.item.id) : undefined}
        siblingQty={siblingQuantity(items, block.item)}
        onDropFlash={onDropFlash ? () => onDropFlash(block.item.id) : undefined}
        onRebalanceFlashGroup={
          onRebalanceFlashGroup
            ? (requestedQuantity) => onRebalanceFlashGroup(block.item.id, requestedQuantity)
            : undefined
        }
        maxQuantity={maxQuantityFor?.(block.item)}
        onEditSerial={() => setEditingItemId(block.item.id)}
        onPickWarehouse={() => setPickingItemId(block.item.id)}
      />
    );

    // TCR-2425 — đơn B2B: bọc dòng thường bằng menu chuột phải "Sao chép dữ liệu
    // cho các dòng bên dưới". Chỉ dòng thường (khối `loose`): cụm combo là nhiều
    // dòng giỏ gộp thành 1 dòng hiển thị, sao chép vào thành phần là ghi đè thứ
    // thu ngân không sửa được bằng tay. `CopyLineValuesMenu` tự trả về fragment
    // (không bọc, không gắn handler) khi `isB2B` false.
    return (
      <CopyLineValuesMenu
        key={block.item.id}
        item={block.item}
        items={items}
        fulfillmentMode={fulfillmentMode}
        isB2B={isB2B}
        hasDiscountPermission={hasDiscountPermission}
        lockFlashSaleLines={lockFlashSaleLines}
        maxQuantityFor={maxQuantityFor}
        onUpdateItem={onUpdateItem}
        idPrefix={variant === 'mobile' ? 'mobile-' : ''}
      >
        {itemRow}
      </CopyLineValuesMenu>
    );
  };

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* `.pos-order-desktop-grid` là vùng cuộn DUY NHẤT của bảng (cả hai trục —
          xem OrderItemsTable.css). Trước đây trục dọc nằm ở div bọc các dòng bên
          dưới; khi cột hàng hẹp hơn bảng, div đó vừa cuộn dọc vừa tự sinh thanh
          cuộn ngang riêng nên các dòng trôi lệch khỏi header. Gộp cả hai trục về
          một vùng cuộn thì header và các dòng luôn cùng bề rộng ⇒ không thể lệch,
          kể cả khi thanh cuộn dọc là loại cổ điển chiếm chỗ (Windows). */}
      {pickableAllItems.length >= 2 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 8px', flexShrink: 0 }}>
          <Button
            size="small"
            data-testid="pos-pick-warehouse-all"
            onClick={() => setPickingAll(true)}
          >
            📦 Chọn kho cho tất cả ({pickableAllItems.length} mã)
          </Button>
        </div>
      )}
      <div className="pos-order-desktop-grid">
        <TableHeader
          showWarehouseCol={showWarehouseCol}
          showSellerCol={showSellerCol}
          showSerialCol={showSerialCol}
          showWeightCol={showWeightCol}
          isB2B={isB2B}
          qtyColWidth={qtyColWidth}
          totalColWidth={totalColWidth}
        />
        <div className="pos-order-desktop-rows">
          {blocks.map((block, blockIndex) => renderBlock(block, blockIndex))}
        </div>
      </div>
      <div className="pos-order-mobile-list">
        {blocks.map((block, blockIndex) => (
          <MobileOrderItemCard key={`mobile-${block.kind === 'combo' ? block.groupId : block.item.id}`}>
            {renderBlock(block, blockIndex, 'mobile')}
          </MobileOrderItemCard>
        ))}
      </div>
      {editingItem && (
        <SerialSwapDialog
          open={!!editingItem}
          onClose={() => setEditingItemId(null)}
          variantId={editingItem.variantId}
          productName={editingItem.variantName}
          currentSerial={editingItem.specificationCode}
          usedSerials={items
            .filter((i) => i.id !== editingItem.id && i.specificationCode)
            .map((i) => i.specificationCode as string)}
          storeId={storeId}
          submitting={swapping}
          onConfirm={handleConfirmSwap}
        />
      )}
      {pickingItem && (
        <WarehousePickerDialog
          open={!!pickingItem}
          onClose={() => setPickingItemId(null)}
          variantId={pickingItem.variantId}
          productName={pickingItem.variantName}
          requiredQty={pickingItem.quantity}
          currentWarehouseId={pickingItem.warehouseId}
          storeId={warehouseStoreId}
          onSelect={(w) => {
            onUpdateItem(pickingItem.id, {
              warehouseId: w?.warehouse_id,
              warehouseName: w?.name,
            });
            // TCR-2475 — đổi kho thì tra lại tồn (cột "Tồn" đọc cache này).
            void queryClient.invalidateQueries({
              queryKey: ['pos', 'variant-warehouse-stocks', pickingItem.variantId],
            });
            setPickingItemId(null);
          }}
        />
      )}
      {pickingAll && pickableAllItems.length > 0 && (
        <ComboWarehousePickerDialog
          open
          onClose={() => setPickingAll(false)}
          title="Chọn kho xuất hàng cho tất cả mã hàng"
          comboName={`${pickableAllItems.length} mã hàng trong giỏ`}
          itemNoun="mã hàng"
          scopeNoun="cả giỏ"
          members={pickableAllItems.map((m) => ({
            itemId: m.id,
            variantId: m.variantId,
            productName: m.variantName,
            requiredQty: m.quantity,
            currentWarehouseId: m.warehouseId,
          }))}
          storeId={warehouseStoreId}
          onApply={(warehouse, { fulfilledItemIds, insufficientItemIds }) => {
            fulfilledItemIds.forEach((itemId) => {
              onUpdateItem(itemId, {
                warehouseId: warehouse?.warehouse_id,
                warehouseName: warehouse?.name,
              });
            });
            if (warehouse && insufficientItemIds.length > 0) {
              void message.warning(
                `Đã gán kho cho ${fulfilledItemIds.length}/${fulfilledItemIds.length + insufficientItemIds.length} mã hàng — ${insufficientItemIds.length} mã không đủ tồn ở kho này, giữ nguyên kho hiện tại, chọn kho riêng cho các dòng đó.`,
              );
            } else if (warehouse) {
              void message.success(`Đã gán kho ${warehouse.name} cho ${fulfilledItemIds.length} mã hàng.`);
            }
            // TCR-2475 — đổi kho hàng loạt ⇒ tra lại tồn của các variant bị đổi kho.
            void queryClient.invalidateQueries({ queryKey: ['pos', 'variant-warehouse-stocks'] });
            setPickingAll(false);
          }}
        />
      )}
      {pickingComboGroupId && pickingComboHead && (
        <ComboWarehousePickerDialog
          open={!!pickingComboGroupId}
          onClose={() => setPickingComboGroupId(null)}
          comboName={pickingComboHead.comboName ?? ''}
          members={pickingComboMembers.map((m) => ({
            itemId: m.id,
            variantId: m.variantId,
            productName: m.variantName,
            requiredQty: m.quantity,
            currentWarehouseId: m.warehouseId,
          }))}
          storeId={warehouseStoreId}
          onApply={(warehouse, { fulfilledItemIds, insufficientItemIds }) => {
            fulfilledItemIds.forEach((itemId) => {
              onUpdateItem(itemId, {
                warehouseId: warehouse?.warehouse_id,
                warehouseName: warehouse?.name,
              });
            });
            if (insufficientItemIds.length > 0) {
              void message.warning(
                `Đã gán kho cho ${fulfilledItemIds.length}/${fulfilledItemIds.length + insufficientItemIds.length} thành phần — ${insufficientItemIds.length} thành phần không đủ tồn ở kho này, chọn kho riêng cho các dòng đó.`,
              );
            }
            // TCR-2475 — đổi kho cả cụm ⇒ tra lại tồn của các variant bị đổi kho.
            void queryClient.invalidateQueries({ queryKey: ['pos', 'variant-warehouse-stocks'] });
            setPickingComboGroupId(null);
          }}
        />
      )}
    </div>
  );
}
